import type { StateController } from '../state/stateController';
import type { TimeOfDayClock } from '../atmosphere/timeOfDayClock';
import type { Transition } from '../transitions/transition';
import type { AudioManager, AudioChannel, SpatialType } from '../audio/audioManager';
import { spatialFalloff } from '../audio/audioManager';
import type { AudioAsset } from '../audio/manifest';
import type { AudioCue } from '../audio/cues';
import { cueChannel, cueAssetId, cueHeroId } from '../audio/cues';
import type { SoundPlacement, Variation } from '../audio/soundscape';
import { spatialForHero } from '../audio/soundscape';
import { newBedId } from '../audio/soundscapeStore';
import type { SaveResult } from '../persist/saveFlows';
import type { SaveHub } from '../persist/saveHub';
import { type SaveVis, paintSave } from './saveVis';
import { isTextEntryTarget } from './keyboardTarget';
import { onDevModeChange } from './devMode';
import { hotkeyRegistry } from './hotkeyRegistry';
import { devBus, focusMatches, type FocusRef } from './devBus';
import { getHeroId, type StateTag } from '../scene/tagging';
import { channelCssColor, applyFocusGlow } from './audioChannelColors';

// Timeline strip — bottom-of-viewport scrubber for the two primary
// drivers of the scene: time of day + past↔present morph.
//
// Layout (TIMELINE_HEIGHT tall, full width, semi-transparent dark panel):
//
//   ┌─────────────────────────────────────────────────────────────┐
//   │ ▶ ⟳ past ⚙   TIME ──────●──────────────────  14:32         │
//   │              CUES ◆────────────◆────────────◆              │
//   │           SOUND  ∞ fan   ∞ generator   ● boombox           │
//   └─────────────────────────────────────────────────────────────┘
//
// Transport (left): play/pause · cycle (skip to noon) · morph era toggle
//            (past ⇄ present) · ⚙ playback config. The morph is a binary era
//            toggle now — the old continuous MORPH scrub lane was removed.
// Time lane: 0..1 mapped to 24h. Tick marks every 6h. Clickable snap
//            markers at dawn/noon/dusk/midnight (label or dot = exact snap).
// Cues lane: authored one-shots scheduled at a time of day. Pick a sound +
//            optional hero + era from the brush on the right, click the track
//            to drop a diamond, drag to move, click to select (✕ deletes),
//            Save (rail · brush · ⌘S) writes soundscape.json. The scheduler
//            fires each as the playhead crosses it (forward, while playing).
// Sound lane: live readout (not a scrub) of every sounding source — chips
//            colored by channel, ∞ = a looping world bed vs a one-shot
//            event, labeled by the hero each is anchored to. Hover a chip to
//            highlight that hero; click to select it.
// Right edge: HH:MM clock chip + state target label.
//
// Hotkeys (registered globally):
//   Space     play/pause time-of-day
//   [ / ]     step time by ±0.5h (≈ ±0.0208 in t units)
//   , / .     step morph progress by ±0.05
//
// The widget reads/writes state directly. devBus events from Phase 1
// keep it in sync if state is mutated elsewhere (snap buttons in
// lil-gui, transition-finish triggers).

const PANEL_BG = 'rgba(18, 22, 28, 0.82)';
const TRACK_BG = 'rgba(255,255,255,0.06)';
const TRACK_FILL = 'rgba(102, 255, 230, 0.18)';
const ACCENT = '#66ffe6';
const MUTED = '#6c7480';
const TEXT = '#e6ebef';
const TICK_COLOR = 'rgba(255,255,255,0.10)';
const SNAP_COLOR = 'rgba(255,255,255,0.35)';

// Full strip height. Exported so other bottom-docked dev surfaces (event
// log, inspector max-height) offset off one number instead of hardcoding it
// — add a lane here and they all stay clear of the strip automatically.
// Fallback / initial reserve for bottom-docked dev surfaces (event log, inspector).
// 2026-06-25: the strip now AUTO-SIZES to its lane content (no fixed height, no
// internal scroll) and publishes its live height as `--lec-timeline-h`; consumers
// prefer that var and fall back to this constant before first layout.
export const TIMELINE_HEIGHT = 248;

// Era-gate ring colors for CUES markers — warm past, cool present (a cue
// with no era gate gets no ring). Distinct from the channel fill so the two
// dimensions — which sound vs which era — don't fight.
const ERA_RING: Record<string, string> = { past: '#e8c170', present: '#7db8e8' };

// Thin per-lane reserve at the right of every time-axis lane (TIME · CUES ·
// BEDS). Holds the CUES/BEDS ▾ brush-reveal (empty on TIME); the full brushes
// open as popovers, so the tracks run nearly full-width while all staying the
// same width — a cue at t still sits under the TIME playhead at t.
const SETTINGS_W = '34px';

const TIME_SNAPS: Array<{ t: number; label: string }> = [
  { t: 0,     label: 'midnight' },
  { t: 0.25,  label: 'dawn' },
  { t: 0.5,   label: 'noon' },
  { t: 0.75,  label: 'dusk' },
];

export interface TimelineOpts {
  clock: TimeOfDayClock;
  state: StateController;
  /** Live audio engine — the SOUND lane reflects whatever it's playing. */
  audio: AudioManager;
  // Morph animation knobs (absorbed from the lil-gui morph folder).
  transitions: Record<string, Transition>;
  setTransition: (name: string) => void;
  initialTransition: string;
  // ── Tier B: time-of-day cues (CUES lane) ──
  /** The live cue list — mutated in place by the lane; read each frame by the
   *  scheduler, so edits take effect immediately. */
  cues: AudioCue[];
  /** The live placement list (beds + music) — the AMBIENCE lane mutates bed
   *  spans/gain in place; the SoundscapeEngine reconciles each frame. */
  placements: SoundPlacement[];
  /** Persist the whole unified doc to soundscape.json. Every save button (rail +
   *  both brushes) and ⌘S call this; the live cues/placements arrays are the
   *  source. Resolves a SaveResult so the buttons render saved ✓ / disk-changed /
   *  save ✕ — making saving visible. `force` overwrites past a freshness conflict. */
  saveSoundscape: (opts?: { force?: boolean }) => Promise<SaveResult>;
  /** Assets available to drop as cues (the picker brush). */
  audioAssets: AudioAsset[];
  /** Hero ids a cue can be spatially anchored to. */
  heroIds: string[];
  /** Count of entries on disk that failed validation at load (S2) — kept
   *  verbatim, not loaded. >0 shows a warning chip so the author knows the file
   *  carries quarantined, non-functional data to fix. */
  droppedCount?: number;
  /** N3 audition-in-context — run a cue's actions through the REAL action path
   *  (engine.run), deterministically (the editor passes the raw actions, no
   *  vary roll: no chance gate, no jitter, no oneOf swap). */
  runActions?: (actions: AudioCue['actions']) => void;
  /** N3 clip audition — play/stop the clip's trimmed slice through the real
   *  chain (spatialized at its anchor). Toggle semantics: call again to stop. */
  auditionClip?: (p: SoundPlacement) => void;
  /** N4 — the music slot's live truth for the persistent strip: occupant,
   *  position, override-riding state, what a flip swaps to. Polled per frame. */
  musicStatus?: () => { assetId: string | null; posSec: number; userSelected: boolean; resume: 'restart' | 'continue'; nextOnFlip: string | null };
  /** N5 — why a bed isn't sounding (lane badge): playing / chance-skipped /
   *  silent. The timeline adds 'missing' itself when the asset isn't loaded. */
  bedState?: (id: string) => 'playing' | 'chance-skipped' | 'silent';
  /** N5 — was this cue's crossing consumed in the current clock-pass? */
  cueFired?: (id: string) => boolean;
  /** N9 — persist the (mutated) audio asset registry; true on a 200. */
  saveAudioManifest?: () => Promise<boolean>;
  /** N9 — register a forked synth asset live so it plays immediately. */
  registerAsset?: (a: AudioAsset) => void;
  /** DM6 — the cross-aspect save coordinator. The timeline registers the
   *  soundscape as a DELEGATE (it keeps its own conflict-aware machine here),
   *  reports its dirtiness for the global readout, schedules a debounced
   *  save-on-commit, and routes ⌘S + the "save all" button to hub.saveAll() so
   *  one keystroke flushes soundscape + heroes + atmosphere together. */
  saveHub?: SaveHub;
}

export function createTimeline(opts: TimelineOpts): {
  root: HTMLElement;
  /** Pop + restore the last cue/bed edit (U1). True if something was undone. */
  undo(): boolean;
  /** DM8 — re-apply the most-recently-undone cue/bed edit. True if something was
   *  redone. Lets the cross-surface ⌘⇧Z walk the timeline too. */
  redo(): boolean;
  /** Timestamp (performance.now) of the most recent undoable edit, 0 if none —
   *  lets the ⌘Z arbiter pick the most-recently-edited surface (vs gizmo undo). */
  lastEditAt(): number;
  /** DM8 — timestamp of the most-recently-undone edit (top of the redo stack),
   *  0 if none — the ⌘⇧Z arbiter's recency key. */
  lastUndoneAt(): number;
  dispose(): void;
} {
  const root = document.createElement('div');
  root.id = 'lec-timeline';
  Object.assign(root.style, {
    position: 'fixed',
    left: '0',
    right: '0',
    bottom: '0',
    // Auto-size to the lane content (no scroll, no slack): the strip is exactly
    // as tall as TIME + CLIPS + BEDS need, growing as clips are authored, capped
    // generously so a pathological count can't fill the screen. The transport's
    // button stack sets the natural minimum height.
    height: 'auto',
    maxHeight: '60vh',
    zIndex: '30',
    background: PANEL_BG,
    color: TEXT,
    font: '11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    display: 'flex',
    alignItems: 'stretch',
    padding: '8px 12px',
    boxSizing: 'border-box',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    userSelect: 'none',
  });

  // ── transport ──────────────────────────────────────────────────────
  const transport = document.createElement('div');
  Object.assign(transport.style, {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    gap: '3px',
    paddingRight: '6px',
    minWidth: '20px',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    marginRight: '8px',
  });
  const playBtn = mkBtn(opts.clock.running ? '◼' : '▶');
  playBtn.title = 'play/pause time (Space)';
  playBtn.addEventListener('click', () => { opts.clock.running = !opts.clock.running; });
  const cycleBtn = mkBtn('⟳');
  cycleBtn.title = 'snap → noon';
  cycleBtn.addEventListener('click', () => { opts.clock.t = 0.5; });
  // Morph era TOGGLE — replaces the old continuous MORPH scrub lane. Shows the
  // current era and flips past ⇄ present on click (animating with the ⚙ morph
  // duration). Sits directly above the ⚙ settings button in the transport rail.
  const morphToggle = mkBtn('past');
  morphToggle.title = 'morph era — click to flip past ⇄ present';
  // Fixed width holds the widest label ('present') so flipping era never resizes
  // the transport rail (and therefore the flex:1 lanes) — sizes stay put across
  // the transition.
  morphToggle.style.minWidth = '66px';
  morphToggle.style.textAlign = 'center';
  morphToggle.style.boxSizing = 'border-box';
  const curEra = (): 'past' | 'present' => (morphValue(opts.state) >= 0.5 ? 'present' : 'past');
  const syncMorphToggle = (): void => {
    const e = curEra();
    if (morphToggle.textContent !== e) morphToggle.textContent = e;
    morphToggle.style.color = e === 'present' ? ACCENT : TEXT;
    morphToggle.style.borderColor = e === 'present' ? ACCENT : 'rgba(255,255,255,0.12)';
  };
  morphToggle.addEventListener('click', () => { opts.state.setTarget(curEra() === 'present' ? 'past' : 'present'); });
  // ⚙ gathers the set-once "playback config" (day length · morph duration ·
  // transition algorithm) into one popover, so it lives in a single place
  // instead of cluttering the lane rows. Populated as those controls are built.
  const gearBtn = mkBtn('⚙');
  gearBtn.title = 'playback config (day length · morph)';

  // ── save state (shared by every save button + ⌘S) ────────────────────
  // Authoring mutates the live cues/placements arrays IN MEMORY — the engine
  // reflects edits instantly — but those edits only reach disk on save. This
  // cluster makes saving VISIBLE so it never "seems unreliable": `dirty` tracks
  // pending edits, and every save button (the always-on rail one + the per-brush
  // ones) renders ONE shared state, so you always know whether your work is
  // persisted. markDirty() is called from every mutation site below.
  const saveButtons: HTMLButtonElement[] = [];
  let dirty = false;
  let saving = false;
  let saveFlashAt = 0; // guards the transient "saved ✓" so a later edit cancels it
  let conflictPending = false; // a freshness conflict awaits reload-or-force
  // S4 — in-flight save race: markDirty bumps editGen; a save captures the gen
  // before awaiting and only clears dirty if it's unchanged at resolve (an edit
  // mid-flight isn't on disk yet). saveQueued remembers a ⌘S that arrived during
  // a save so exactly one trailing save fires after it resolves.
  let editGen = 0;
  let saveQueued = false;
  // DM6 — save-on-commit: a debounced auto-save so a finished edit reaches disk
  // without a separate ⌘S. Gated on the hub's autoSave flag (one switch governs
  // every aspect); with no hub the timeline keeps its old manual-save behavior.
  const AUTOSAVE_MS = 900;
  let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleAutoSave = (): void => {
    if (!opts.saveHub?.autoSaveOn()) return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => { autoSaveTimer = null; void runSave(); }, AUTOSAVE_MS);
  };
  // DM5 — the save-state vocabulary (clean/dirty/saving/saved/failed/conflict)
  // now lives in the shared `saveVis` module, so the timeline transport and the
  // inspector's per-aspect buttons speak the same language and can't drift.
  const SAVE_DEFAULT_TITLE = 'Save cues + beds to soundscape.json (this aspect only — ⌘S saves all)';
  const setSaveVis = (vis: SaveVis): void => { for (const b of saveButtons) paintSave(b, vis, SAVE_DEFAULT_TITLE); };

  // U1 — timeline undo. markDirty is the single chokepoint every cue/bed edit
  // already funnels through. It pushes the PRE-edit snapshot (lastSnapshot, the
  // state captured after the previous edit) and then re-clones the now-current
  // state — so a snapshot taken AFTER the mutation still rolls back correctly.
  // JSON clone is exact for this data (it's the same shape that serializes to
  // soundscape.json) and never throws on a stray function. `at` lets the ⌘Z
  // arbiter compare recency against the gizmo undo stack (last-edit-wins).
  const UNDO_CAP = 60;
  const cloneState = (): { placements: SoundPlacement[]; cues: AudioCue[] } => ({
    placements: JSON.parse(JSON.stringify(opts.placements)),
    cues: JSON.parse(JSON.stringify(opts.cues)),
  });
  let lastSnapshot = cloneState();
  type UndoSnap = { placements: SoundPlacement[]; cues: AudioCue[]; at: number };
  const undoStack: UndoSnap[] = [];
  // DM8 — a redo stack so ⌘⇧Z walks across surfaces (the timeline used to have
  // no redo at all). undoTimeline pushes the pre-undo (post-edit) state here;
  // redoTimeline re-applies it. A fresh edit (markDirty) forks history → cleared.
  const redoStack: UndoSnap[] = [];
  const lastEditAt = (): number => (undoStack.length ? undoStack[undoStack.length - 1].at : 0);
  const lastUndoneAt = (): number => (redoStack.length ? redoStack[redoStack.length - 1].at : 0);
  const markDirty = (): void => {
    undoStack.push({ placements: lastSnapshot.placements, cues: lastSnapshot.cues, at: performance.now() });
    if (undoStack.length > UNDO_CAP) undoStack.shift();
    redoStack.length = 0; // DM8 — a new edit invalidates the redo branch
    lastSnapshot = cloneState();
    dirty = true; editGen++; saveFlashAt = 0; setSaveVis('dirty');
    opts.saveHub?.noteDelegateChanged(); // DM6 — light the global unsaved readout
    scheduleAutoSave();                   // DM6 — save-on-commit
  };
  // U3 — protect an in-flight cue/bed drag from the era-flip re-render. While a
  // drag holds pointer capture, the rAF must NOT re-render the lanes (that
  // destroys the captured element, so pointerup never arrives and the edit is
  // silently lost). Defer the re-render to drag end. flagDirtyDuringDrag also
  // sets dirty on the first effective move, so even a lost pointerup can't leave
  // an unsaved divergence the beforeunload guard wouldn't catch.
  let dragInProgress = false;
  let pendingEraRender = false;
  const beginDrag = (): void => { dragInProgress = true; };
  const endDrag = (): void => {
    dragInProgress = false;
    if (pendingEraRender) { pendingEraRender = false; renderCues(); renderAmbience(); renderClips(); }
  };
  const flagDirtyDuringDrag = (): void => { dirty = true; editGen++; setSaveVis('dirty'); };
  const runSave = async (): Promise<void> => {
    if (saving) { saveQueued = true; return; } // a save is in flight — remember to re-run once it resolves
    if (!dirty) return; // dirty gate (S1): no edits → no write (⌘S in a pristine tab is a no-op)
    saving = true; saveQueued = false; setSaveVis('saving');
    const genAtStart = editGen; // edits after this aren't in the bytes we're about to send
    // After a conflict, the next deliberate save forces (overwrites disk); a
    // browser reload is the other resolution (re-bases on the disk copy).
    const force = conflictPending;
    let result: SaveResult = 'failed';
    try { result = await opts.saveSoundscape(force ? { force: true } : undefined); } catch { result = 'failed'; }
    saving = false;
    const editedDuringFlight = editGen !== genAtStart;
    if (result === 'saved') {
      conflictPending = false;
      if (editedDuringFlight) {
        // The bytes on disk DON'T include the mid-flight edit — stay dirty so the
        // beforeunload guard holds and a trailing save persists it (S4).
        dirty = true; setSaveVis('dirty');
      } else {
        dirty = false;
        const stamp = (saveFlashAt = performance.now());
        setSaveVis('saved');
        // Back to a quiet "save" after a beat — unless a new edit dirtied us since.
        setTimeout(() => { if (!dirty && saveFlashAt === stamp) setSaveVis('clean'); }, 1500);
      }
    } else if (result === 'conflict') {
      // Refused: the file changed on disk. Stay dirty, hold the conflict visual
      // (no auto-revert) until the user reloads or forces the next save.
      conflictPending = true;
      setSaveVis('conflict');
    } else {
      // Genuine failure (network/server) — a plain retry is right, so fall back
      // to the unsaved indicator after flashing the failure.
      conflictPending = false;
      setSaveVis('failed');
      setTimeout(() => { if (dirty) setSaveVis('dirty'); }, 2500);
    }
    opts.saveHub?.noteDelegateChanged(); // DM6 — refresh the global readout as soundscape settles
    devBus.emit('save:result', { aspect: 'soundscape', result }); // DM14 — narrate the save
    // Trailing save (S4): if an edit or a second ⌘S arrived mid-flight and the
    // save succeeded, fire exactly one more (coalesced — at most one re-run per
    // in-flight save). Not on conflict (would auto-force) or failure (retry loop).
    if (result === 'saved' && dirty && (editedDuringFlight || saveQueued)) {
      saveQueued = false;
      void runSave();
    }
  };
  // DM6 — register the soundscape as a hub DELEGATE: it keeps its own
  // conflict-aware machine (above), but the hub can flush it from save-all and
  // reads its dirtiness for the global readout. saveAll's force-on-conflict for
  // OWNED aspects is matched here by runSave's own conflictPending→force path.
  opts.saveHub?.registerDelegate('soundscape', { isDirty: () => dirty, flush: () => runSave() });
  const saveAllOrSoundscape = (): void => {
    if (opts.saveHub) void opts.saveHub.saveAll();
    else void runSave();
  };
  const mkSaveBtn = (): HTMLButtonElement => {
    const b = mkBtn('save');
    b.style.fontSize = '10px';
    b.style.minWidth = '48px';   // fixed so saving…/saved ✓ never reflow the layout
    b.style.textAlign = 'center';
    b.addEventListener('click', () => void runSave());
    saveButtons.push(b);
    paintSave(b, dirty ? 'dirty' : 'clean'); // also sets the default title
    return b;
  };
  // Always-visible save lives in the transport rail (the global-controls column)
  // so it's reachable without opening a brush — its color doubles as the unsaved
  // indicator for the soundscape (its per-aspect save).
  const saveBtnRail = mkSaveBtn();

  // DM6 — SAVE-ALL: one button (⌘S maps to it) that flushes every dirty aspect —
  // soundscape + hero placements + atmosphere — so the conductor never hunts for
  // the right per-aspect button. It also IS the global unsaved readout the audit
  // found missing: its color/label reflect the worst state across all aspects, and
  // a count when several diverge. The per-aspect buttons (soundscape rail above,
  // inspector's manifest/atmosphere) stay — this is additive.
  const saveAllBtn = mkBtn('save all');
  saveAllBtn.style.fontSize = '10px';
  saveAllBtn.style.minWidth = '62px';
  saveAllBtn.style.textAlign = 'center';
  const SAVEALL_TITLE = 'Save everything outstanding — soundscape · heroes · atmosphere (⌘S / Ctrl+S)';
  saveAllBtn.addEventListener('click', () => saveAllOrSoundscape());
  const paintSaveAll = (): void => {
    if (!opts.saveHub) { saveAllBtn.style.display = 'none'; return; }
    const vis = opts.saveHub.globalVis();
    paintSave(saveAllBtn, vis, SAVEALL_TITLE);
    // Resting / dirty labels read "save all" (paintSave's generic 'save'/'● save'
    // wording is per-aspect); the transient saving/saved/✕ states keep theirs.
    if (vis === 'clean') saveAllBtn.textContent = 'save all';
    else if (vis === 'dirty') {
      const n = opts.saveHub.dirtyCount();
      saveAllBtn.textContent = n > 1 ? `● save all (${n})` : '● save all';
    }
  };
  const unsubHub = opts.saveHub?.onChange(paintSaveAll);
  paintSaveAll();

  // Quarantine warning chip (S2): shown only when the loaded soundscape.json had
  // entries that failed to parse. They were kept verbatim (and round-trip on
  // save) but aren't functional — this tells the author the file needs a fix.
  const dropN = opts.droppedCount ?? 0;
  const dropChip = document.createElement('div');
  if (dropN > 0) {
    dropChip.textContent = `⚠ ${dropN}`;
    dropChip.title = `${dropN} entr${dropN === 1 ? 'y' : 'ies'} in soundscape.json failed to parse — kept verbatim, not loaded. Fix the source; saving preserves them.`;
    Object.assign(dropChip.style, { fontSize: '9px', color: '#ffb454', textAlign: 'center', lineHeight: '1.2', cursor: 'default' });
  }

  // N5 — the silent-schedule banner: the clock can run while the AudioContext
  // is still suspended (no first gesture yet), which reads as broken during a
  // performance. Say it instead. Painted in refresh().
  const silentBanner = document.createElement('div');
  silentBanner.textContent = '⚠ schedule running silently — click anywhere to enable sound';
  Object.assign(silentBanner.style, {
    position: 'absolute', top: '-22px', left: '50%', transform: 'translateX(-50%)',
    display: 'none', padding: '3px 10px', background: 'rgba(40,30,8,0.92)',
    border: '1px solid #ffd87c', borderRadius: '4px', color: '#ffd87c',
    fontSize: '10px', whiteSpace: 'nowrap', zIndex: '50', pointerEvents: 'none',
  });
  root.appendChild(silentBanner);

  // Compact transport — the buttons sit in a thin left rail.
  for (const b of [playBtn, cycleBtn, morphToggle, gearBtn]) { b.style.fontSize = '10px'; b.style.padding = '1px 3px'; b.style.lineHeight = '1'; }
  const configPop = document.createElement('div');
  Object.assign(configPop.style, {
    position: 'absolute', bottom: 'calc(100% + 6px)', left: '8px', zIndex: '40',
    display: 'none', flexDirection: 'column', gap: '6px', padding: '8px 10px',
    background: 'rgba(12,15,20,0.97)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '6px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.45)', minWidth: '150px',
  });
  const configHead = document.createElement('div');
  configHead.textContent = 'playback';
  Object.assign(configHead.style, { color: MUTED, fontSize: '8px', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '1px' });
  configPop.appendChild(configHead);
  gearBtn.addEventListener('click', () => { configPop.style.display = configPop.style.display === 'none' ? 'flex' : 'none'; });
  // Helper: wrap a (label, control) as a popover row.
  const configRow = (labelText: string, control: HTMLElement): HTMLElement => {
    const r = document.createElement('div');
    Object.assign(r.style, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' });
    const l = document.createElement('span'); l.textContent = labelText;
    Object.assign(l.style, { color: MUTED, fontSize: '9px' });
    r.append(l, control); return r;
  };
  transport.appendChild(playBtn);
  transport.appendChild(cycleBtn);
  transport.appendChild(morphToggle);
  transport.appendChild(gearBtn);
  transport.appendChild(saveBtnRail);
  transport.appendChild(saveAllBtn);
  if (dropN > 0) transport.appendChild(dropChip);
  root.appendChild(transport);
  root.appendChild(configPop);

  // ── lanes container ────────────────────────────────────────────────
  const lanes = document.createElement('div');
  Object.assign(lanes.style, {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    // Pack lanes from the top with a tight, uniform gap (was space-around, which
    // padded big gaps above/below every lane — esp. around BEDS). The strip
    // height is sized to the content so there's no slack to distribute.
    justifyContent: 'flex-start',
    minHeight: '0',
  });
  root.appendChild(lanes);

  // ── cross-surface focus (hover-to-illuminate) ───────────────────────
  // A hover on ANY audio surface (here, the inspector, or a 3D source sphere)
  // broadcasts an audio:focus carrying whatever ids it knows; every other
  // surface lights the elements it matches, in that sound's channel color.
  // Each lane element stashes its FocusRef identity + the inner node to glow;
  // applyFocus walks the three live element maps when the focus changes, and
  // bindFocus paints an element's initial state at (re)create time. We also
  // re-broadcast on our own chip/bar/marker hovers (emitFocus).
  // `currentFocus` is the transient hover; `pinnedFocus` is the persistent
  // selection echo — set to {heroId} whenever a hero is selected (a 3D click,
  // a SOUND-chip click, an inspector row, or selecting one of THESE beds/cues),
  // so the active hero's beds + cues stay lit while you edit them. An element
  // glows if either matches.
  let currentFocus: FocusRef | null = null;
  let pinnedFocus: FocusRef | null = null;
  type FocusEl = HTMLElement & { _fcand?: FocusRef; _fch?: string; _fglow?: HTMLElement };
  const emitFocus = (ref: FocusRef | null): void => devBus.emit('audio:focus', { ref });
  const paintFocusEl = (el: FocusEl): void => {
    if (!el._fcand) return;
    const on = focusMatches(currentFocus, el._fcand) || focusMatches(pinnedFocus, el._fcand);
    applyFocusGlow(el._fglow ?? el, on, el._fch ?? '');
  };
  const bindFocus = (el: HTMLElement, cand: FocusRef, channel: string, glow?: HTMLElement): void => {
    const f = el as FocusEl; f._fcand = cand; f._fch = channel; f._fglow = glow;
    paintFocusEl(f);
  };
  // B2 — hover-glow backstop. Per-element mouseleave can be missed (a fast move,
  // an element re-rendered mid-hover, or leaving through a gap), stranding the
  // transient glow lit. Clearing currentFocus when the pointer leaves the whole
  // timeline guarantees "hover away clears within a frame". pinnedFocus (the
  // persistent selection echo) is untouched — only the transient hover clears.
  root.addEventListener('pointerleave', () => emitFocus(null));

  // W3b — the dev lanes map the clock's daylight window [dayStart, dayEnd] to
  // their full 0..1 width (the whole lane = the daylight experience; no dead
  // night). t↔lane-fraction convert through these; daySecs/dur are unchanged.
  // A region of `dur` seconds spans `dur/daySecs()/daySpan()` of the lane.
  const daySpan = (): number => Math.max(1e-6, opts.clock.dayEnd - opts.clock.dayStart);
  const tToFrac = (t: number): number => (t - opts.clock.dayStart) / daySpan();
  const fracToT = (f: number): number => opts.clock.dayStart + f * daySpan();

  // ── time lane ──────────────────────────────────────────────────────
  const timeLane = mkLane('TIME');
  lanes.appendChild(timeLane.row);
  // Tick marks every 6h, positioned within the daylight window (W3b — off-window
  // marks like 0h/24h fall outside 0..100% and clip away).
  for (let i = 0; i <= 4; i++) {
    const f = tToFrac(i * 0.25);
    if (f < 0 || f > 1) continue;
    const tick = document.createElement('div');
    Object.assign(tick.style, {
      position: 'absolute',
      left: `${f * 100}%`,
      top: '0', bottom: '0',
      width: '1px',
      background: TICK_COLOR,
      pointerEvents: 'none',
    });
    timeLane.track.appendChild(tick);
  }
  // Snap labels above the track.
  for (const s of TIME_SNAPS) {
    const sf = tToFrac(s.t);
    if (sf < 0 || sf > 1) continue; // W3b — a snap outside the daylight window (e.g. midnight) is dropped
    const snapTo = (): void => { opts.clock.t = s.t; };
    const label = document.createElement('div');
    label.textContent = s.label;
    label.title = `snap → ${s.label}`;
    Object.assign(label.style, {
      position: 'absolute',
      left: `${sf * 100}%`,
      top: '-12px',
      transform: 'translateX(-50%)',
      color: MUTED,
      fontSize: '9px',
      whiteSpace: 'nowrap',
    });
    makeSnap(label, snapTo, (on) => { label.style.color = on ? ACCENT : MUTED; });
    timeLane.track.appendChild(label);
    // Clickable dot on the track at the snap point (exact snap).
    const dot = document.createElement('div');
    Object.assign(dot.style, {
      position: 'absolute',
      left: `${sf * 100}%`,
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '5px', height: '5px',
      borderRadius: '50%',
      background: SNAP_COLOR,
    });
    makeSnap(dot, snapTo, (on) => {
      dot.style.background = on ? ACCENT : SNAP_COLOR;
      dot.style.transform = on ? 'translate(-50%, -50%) scale(1.5)' : 'translate(-50%, -50%)';
    });
    timeLane.track.appendChild(dot);
  }
  // U7 — a TIME-lane drag pauses the clock so sweeping the playhead across a
  // cue cluster doesn't machine-gun every crossed cue (MAX_STEP only swallows
  // jumps > 0.25/frame; drag deltas are tiny). The pre-drag running state is
  // stashed and restored on release; Space mid-drag edits the STASH (see the
  // Space handler) so the user's intent survives the restore. Discrete jumps
  // (snap labels, [/] steps) still single-fire crossed cues — that matches the
  // documented crossing semantics, kept deliberately.
  let timeScrubStash: boolean | null = null; // null = no TIME drag in flight
  wireScrub(timeLane.track, timeLane.fill, timeLane.head, (m) => {
    opts.clock.t = fracToT(m); // W3b — track frac → windowed t
  }, () => tToFrac(opts.clock.t), {
    onStart: () => { timeScrubStash = opts.clock.running; opts.clock.running = false; },
    onEnd: () => {
      if (timeScrubStash !== null) { opts.clock.running = timeScrubStash; timeScrubStash = null; }
    },
  });

  // ── time settings → ⚙ popover ───────────────────────────────────────
  // Day length is set-once config, so it lives in the ⚙ popover now. The lane
  // keeps an empty settings spacer of SETTINGS_W so the TIME track stays the
  // same width as CUES/BEDS — a cue at t still sits under the TIME playhead at t.
  const timeSettings = document.createElement('div');
  Object.assign(timeSettings.style, { flexShrink: '0', width: SETTINGS_W });
  const dayInput = document.createElement('input');
  dayInput.type = 'number'; dayInput.min = '30'; dayInput.max = '1800'; dayInput.step = '10';
  dayInput.value = String(opts.clock.dayLengthSeconds);
  dayInput.title = 'full day length (seconds)';
  Object.assign(dayInput.style, { width: '60px', background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', font: 'inherit', fontSize: '10px', padding: '1px 3px' });
  dayInput.addEventListener('change', () => {
    const v = Number(dayInput.value);
    if (v >= 1) opts.clock.dayLengthSeconds = v;
  });
  configPop.appendChild(configRow('day length (s)', dayInput));
  timeLane.row.appendChild(timeSettings);

  // ── morph config → ⚙ popover ────────────────────────────────────────
  // The morph is authored as a binary era TOGGLE (transport rail, above) — the
  // old continuous MORPH scrub lane is gone. Its animation config lives here in
  // the ⚙ popover: morph duration + transition algorithm.
  const durInput = document.createElement('input');
  durInput.type = 'number'; durInput.min = '0'; durInput.max = '5'; durInput.step = '0.1';
  durInput.value = String(opts.state.duration);
  durInput.title = 'morph duration (s)';
  Object.assign(durInput.style, { width: '60px', background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', font: 'inherit', fontSize: '10px', padding: '1px 3px' });
  durInput.addEventListener('change', () => { opts.state.duration = Math.max(0, Number(durInput.value) || 0); });
  const transSel = document.createElement('select');
  Object.assign(transSel.style, { background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', font: 'inherit', fontSize: '10px', padding: '1px 2px' });
  for (const name of Object.keys(opts.transitions)) {
    const o = document.createElement('option'); o.value = name; o.textContent = name; transSel.appendChild(o);
  }
  transSel.value = opts.initialTransition;
  transSel.title = 'transition algorithm';
  transSel.addEventListener('change', () => opts.setTransition(transSel.value));
  configPop.appendChild(configRow('morph dur (s)', durInput));
  configPop.appendChild(configRow('transition', transSel));

  // Shared <select> styling for the lane brushes.
  const selStyle = { background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', font: 'inherit', fontSize: '10px', padding: '1px 2px' };

  // ── N3 editor field helpers (shared by the cue + clip popovers) ───────────
  /** Labeled mini-row: a muted caption next to a control. */
  const edRow = (label: string, control: HTMLElement): HTMLElement => {
    const r = document.createElement('div');
    Object.assign(r.style, { display: 'flex', alignItems: 'center', gap: '4px' });
    const l = document.createElement('span');
    l.textContent = label;
    Object.assign(l.style, { color: MUTED, fontSize: '8px', width: '26px', flexShrink: '0' });
    r.append(l, control);
    return r;
  };
  /** Numeric input committing on change/Enter; clamp + writeback via onCommit. */
  const edNum = (value: number | undefined, step: number, title: string, onCommit: (v: number) => void, placeholder = ''): HTMLInputElement => {
    const i = document.createElement('input');
    i.type = 'number'; i.step = String(step);
    i.value = value !== undefined ? String(Math.round(value * 1000) / 1000) : '';
    i.placeholder = placeholder; i.title = title;
    Object.assign(i.style, { ...selStyle, width: '48px', fontSize: '9px' });
    i.addEventListener('keydown', (e) => e.stopPropagation());
    i.addEventListener('change', () => {
      const v = parseFloat(i.value);
      if (Number.isFinite(v)) onCommit(v);
    });
    return i;
  };
  /** Small select with stopPropagation baked in. */
  const edSel = (entries: Array<{ v: string; label: string }>, value: string, title: string, onChange: (v: string) => void): HTMLSelectElement => {
    const s = document.createElement('select');
    Object.assign(s.style, { ...selStyle, fontSize: '9px' });
    s.title = title;
    for (const e of entries) { const o = document.createElement('option'); o.value = e.v; o.textContent = e.label; s.appendChild(o); }
    s.value = value;
    s.addEventListener('pointerdown', (e) => e.stopPropagation());
    s.addEventListener('change', () => onChange(s.value));
    return s;
  };

  // ── N9 — patch instruments at the desk ────────────────────────────────────
  // A GENERIC renderer over the patch JSON itself (not hand-built per patch):
  // every continuous param becomes a drag-able range; edits mutate the live
  // registry object, so the popover's audition (and the next scheduled play)
  // sounds the tweak immediately — voices are built per play() (A13).
  const edRange = (label: string, value: number, min: number, max: number, step: number, onInput: (v: number) => void): HTMLElement => {
    const r = document.createElement('div');
    Object.assign(r.style, { display: 'flex', alignItems: 'center', gap: '4px' });
    const l = document.createElement('span');
    l.textContent = label;
    Object.assign(l.style, { color: MUTED, fontSize: '8px', width: '44px', flexShrink: '0' });
    const range = document.createElement('input');
    range.type = 'range'; range.min = String(min); range.max = String(max); range.step = String(step);
    range.value = String(value);
    Object.assign(range.style, { flex: '1 1 auto', height: '10px' });
    const val = document.createElement('span');
    val.textContent = String(Math.round(value * 100) / 100);
    Object.assign(val.style, { color: TEXT, fontSize: '8px', width: '34px', textAlign: 'right' });
    range.addEventListener('pointerdown', (e) => e.stopPropagation());
    range.addEventListener('input', () => {
      const v = parseFloat(range.value);
      val.textContent = String(Math.round(v * 100) / 100);
      onInput(v);
    });
    r.append(l, range, val);
    return r;
  };
  /** The patch section for a synth asset, or null for samples. `onFork` lets
   *  the host popover re-point its cue/clip at the forked sibling. */
  const makePatchSection = (assetId: string | undefined, onFork: (newId: string) => void): HTMLElement | null => {
    const asset = assetId ? opts.audioAssets.find((a) => a.id === assetId) : undefined;
    const patch = asset?.kind === 'synth' ? asset.patch : undefined;
    if (!asset || !patch) return null;
    const sec = document.createElement('div');
    Object.assign(sec.style, { display: 'flex', flexDirection: 'column', gap: '2px', borderTop: '1px solid rgba(255,255,255,0.10)', marginTop: '3px', paddingTop: '3px', minWidth: '190px' });
    const head = document.createElement('div');
    head.textContent = `⌁ patch · ${asset.id}`;
    Object.assign(head.style, { color: MUTED, fontSize: '8px', letterSpacing: '0.4px' });
    sec.appendChild(head);
    (patch.voices ?? []).forEach((v, i) => {
      if (v.type === 'osc') {
        sec.append(
          edRange(`osc${i} hz`, v.freq ?? 440, 20, 2000, 1, (x) => { v.freq = x; }),
          edRange(`osc${i} gain`, v.gain ?? 0.5, 0, 1, 0.01, (x) => { v.gain = x; }),
        );
        if (v.detune !== undefined) sec.appendChild(edRange(`osc${i} det`, v.detune, -100, 100, 1, (x) => { v.detune = x; }));
      } else {
        sec.appendChild(edRange(`noise${i}`, v.gain ?? 0.5, 0, 1, 0.01, (x) => { v.gain = x; }));
      }
    });
    if (patch.filter) {
      sec.append(
        edRange('filt hz', patch.filter.freq, 40, 8000, 5, (x) => { patch.filter!.freq = x; }),
        edRange('filt q', patch.filter.q ?? 1, 0.1, 20, 0.1, (x) => { patch.filter!.q = x; }),
      );
    }
    const env = patch.env ?? (patch.env = {});
    sec.append(
      edRange('attack', env.attack ?? 0.01, 0.001, 2, 0.005, (x) => { env.attack = x; }),
      edRange('sustain', env.sustain ?? 1, 0, 1, 0.01, (x) => { env.sustain = x; }),
      edRange('release', env.release ?? 0.1, 0.01, 2, 0.01, (x) => { env.release = x; }),
    );
    if (env.decay !== undefined) sec.appendChild(edRange('decay', env.decay, 0.01, 2, 0.01, (x) => { env.decay = x; }));
    if (patch.lfo) {
      sec.append(
        edRange('lfo hz', patch.lfo.rateHz, 0.05, 20, 0.05, (x) => { patch.lfo!.rateHz = x; }),
        edRange('lfo dep', patch.lfo.depth, 0, patch.lfo.target === 'filter' ? 2000 : (patch.lfo.target === 'pitch' ? 1200 : 1), patch.lfo.target === 'gain' ? 0.01 : 1, (x) => { patch.lfo!.depth = x; }),
      );
    }
    if (patch.dur !== undefined) sec.appendChild(edRange('dur s', patch.dur, 0.05, 8, 0.05, (x) => { patch.dur = x; }));
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: '4px', marginTop: '2px' });
    if (opts.saveAudioManifest) {
      const save = mkBtn('save patches');
      save.style.fontSize = '8.5px';
      save.title = 'write the registry (all patch tweaks) → public/audio/manifest.json';
      save.addEventListener('pointerdown', (e) => e.stopPropagation());
      save.addEventListener('click', async (e) => {
        e.stopPropagation();
        save.textContent = 'saving…';
        const ok = await opts.saveAudioManifest!();
        save.textContent = ok ? 'saved ✓' : 'save ✕';
        save.style.color = ok ? '#6acf91' : '#ff8a8a';
        setTimeout(() => { save.textContent = 'save patches'; save.style.color = ''; }, 1500);
      });
      row.appendChild(save);
    }
    const fork = mkBtn('⑂ fork');
    fork.style.fontSize = '8.5px';
    fork.title = 'duplicate as a new named patch (license: project) and point this cue/clip at it — the original is untouched';
    fork.addEventListener('pointerdown', (e) => e.stopPropagation());
    fork.addEventListener('click', (e) => {
      e.stopPropagation();
      const name = window.prompt('New patch id?', `${asset.id}_v2`);
      if (!name || opts.audioAssets.some((a) => a.id === name)) return;
      const clone: AudioAsset = { ...asset, id: name, license: 'project', patch: JSON.parse(JSON.stringify(patch)) };
      opts.audioAssets.push(clone);
      opts.registerAsset?.(clone);
      onFork(name);
    });
    row.appendChild(fork);
    sec.appendChild(row);
    return sec;
  };

  /** Asset options for a swap dropdown — same-channel assets first (findability)
   *  then the rest, synth patches alongside samples (marked ⌁); the current
   *  asset always present. Swapping sample ↔ synth is a data-only act (A13). */
  const assetEntriesFor = (channel: string, current?: string): Array<{ v: string; label: string }> => {
    const tag = (a: { id: string; kind?: string }): string => `${a.kind === 'synth' ? '⌁ ' : ''}${prettyAsset(a.id)}`;
    const same = opts.audioAssets.filter((a) => (a.defaultChannel ?? 'sfx') === channel);
    const rest = opts.audioAssets.filter((a) => (a.defaultChannel ?? 'sfx') !== channel);
    const seen = new Set<string>();
    const entries: Array<{ v: string; label: string }> = [];
    for (const a of [...same, ...rest]) {
      if (seen.has(a.id)) continue; seen.add(a.id);
      entries.push({ v: a.id, label: tag(a) });
    }
    if (current && !seen.has(current)) entries.unshift({ v: current, label: prettyAsset(current) });
    return entries;
  };
  // The "day" the schedule lanes show: the era you're morphed toward. Flips at
  // the halfway point so toggling MORPH past↔present repopulates CUES + BEDS.
  // (Decoupled from StateController.current, which only settles at progress=1.)
  const displayEra = (): StateTag => (morphValue(opts.state) >= 0.5 ? 'present' : 'past');

  // Compact variation editor appended to a selected bed/cue popover. Mutates the
  // item's `vary` block in place (engine reflects it on the next re-roll/fire),
  // so no re-render is needed — keeping slider focus while dragging. `getArmed`
  // feeds the oneOf "+pool" button (the BEDS armed loop, or the CUES brush).
  const numStyle = { width: '34px', background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', font: 'inherit', fontSize: '8.5px' };
  const makeVaryEditor = (
    item: { vary?: Variation },
    isCue: boolean,
    getArmed: () => string | null,
    baseAsset: string,
    onEdit: () => void,
  ): HTMLElement => {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px', fontSize: '8.5px', color: MUTED });
    row.addEventListener('pointerdown', (e) => e.stopPropagation()); // don't start a bar/marker drag
    const ensure = (): Variation => (item.vary ??= {});
    const clean = (): void => { if (item.vary && Object.keys(item.vary).length === 0) item.vary = undefined; };

    const tag = document.createElement('span'); tag.textContent = 'vary'; row.appendChild(tag);
    // chance
    const chanceIn = document.createElement('input');
    chanceIn.type = 'range'; chanceIn.min = '0'; chanceIn.max = '1'; chanceIn.step = '0.05';
    chanceIn.value = String(item.vary?.chance ?? 1); chanceIn.style.width = '44px'; chanceIn.title = 'chance to play';
    const chanceLbl = document.createElement('span');
    const showChance = (): void => { chanceLbl.textContent = `${Math.round((item.vary?.chance ?? 1) * 100)}%`; };
    showChance();
    // DM15 — live on 'input' (engine reflects it), commit (undo) on 'change' so
    // one slider drag = one undo step. Same split on detune/jitter below.
    chanceIn.addEventListener('input', () => {
      const v = Number(chanceIn.value);
      if (v >= 1) { if (item.vary) delete item.vary.chance; } else ensure().chance = v;
      clean(); showChance();
    });
    chanceIn.addEventListener('change', onEdit);
    row.append(chanceIn, chanceLbl);
    // detune
    const detIn = document.createElement('input');
    detIn.type = 'number'; detIn.min = '0'; detIn.max = '400'; detIn.step = '10';
    detIn.value = String(item.vary?.detune ?? 0); detIn.title = 'detune ± cents';
    Object.assign(detIn.style, numStyle);
    detIn.addEventListener('input', () => {
      const v = Math.max(0, Number(detIn.value) || 0);
      if (v === 0) { if (item.vary) delete item.vary.detune; } else ensure().detune = v;
      clean();
    });
    detIn.addEventListener('change', onEdit); // DM15
    const detTag = document.createElement('span'); detTag.textContent = '¢';
    row.append(detIn, detTag);
    // jitter (cues only)
    if (isCue) {
      const jitIn = document.createElement('input');
      jitIn.type = 'number'; jitIn.min = '0'; jitIn.max = '10'; jitIn.step = '0.1';
      jitIn.value = String(item.vary?.jitterSec ?? 0); jitIn.title = 'jitter (s)';
      Object.assign(jitIn.style, numStyle);
      jitIn.addEventListener('input', () => {
        const v = Math.max(0, Number(jitIn.value) || 0);
        if (v === 0) { if (item.vary) delete item.vary.jitterSec; } else ensure().jitterSec = v;
        clean();
      });
      jitIn.addEventListener('change', onEdit); // DM15
      const jitTag = document.createElement('span'); jitTag.textContent = 's';
      row.append(jitIn, jitTag);
    }
    // oneOf pool: ⊕ adds the armed sound (seeding the base first); ⌫ clears.
    const poolBtn = document.createElement('span');
    const showPool = (): void => { poolBtn.textContent = `⊕${item.vary?.oneOf?.length ?? 0}`; };
    showPool();
    poolBtn.title = 'add the armed sound to the variant pool';
    Object.assign(poolBtn.style, { cursor: 'pointer', color: ACCENT, border: `1px solid ${ACCENT}55`, borderRadius: '3px', padding: '0 3px' });
    poolBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); e.preventDefault();
      const armed = getArmed();
      if (!armed) return;
      const v = ensure();
      if (!v.oneOf) v.oneOf = [baseAsset];
      if (!v.oneOf.includes(armed)) v.oneOf.push(armed);
      showPool(); onEdit();
    });
    const poolClr = document.createElement('span');
    poolClr.textContent = '⌫'; poolClr.title = 'clear the variant pool';
    Object.assign(poolClr.style, { cursor: 'pointer', color: '#ff8a8a' });
    poolClr.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); e.preventDefault();
      if (item.vary) delete item.vary.oneOf;
      clean(); showPool(); onEdit();
    });
    row.append(poolBtn, poolClr);
    return row;
  };

  // Bed-only modulation editor: gain breath (depth + period) + loop-to-loop
  // reshuffle. Mutates p.mod live; the engine LFO/reshuffle reflect it.
  const makeModEditor = (p: SoundPlacement, onEdit: () => void): HTMLElement => {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', fontSize: '8.5px', color: MUTED });
    row.addEventListener('pointerdown', (e) => e.stopPropagation());
    const ensure = (): NonNullable<SoundPlacement['mod']> => (p.mod ??= {});
    const clean = (): void => { if (p.mod && Object.keys(p.mod).length === 0) p.mod = undefined; };
    const tag = document.createElement('span'); tag.textContent = 'mod'; row.appendChild(tag);
    // breath depth
    const depth = document.createElement('input');
    depth.type = 'range'; depth.min = '0'; depth.max = '1'; depth.step = '0.05';
    depth.value = String(p.mod?.depth ?? 0); depth.style.width = '44px'; depth.title = 'breath depth';
    const depthLbl = document.createElement('span');
    const showD = (): void => { depthLbl.textContent = `${Math.round((p.mod?.depth ?? 0) * 100)}%`; };
    showD();
    // DM15 — 'input' updates the value LIVE (the engine breathes as you drag),
    // but the undo-committing onEdit() defers to 'change' so one drag = one undo
    // step (was ~20 entries/drag). Same split on every mod control below.
    depth.addEventListener('input', () => {
      const v = Number(depth.value);
      if (v <= 0) { if (p.mod) delete p.mod.depth; } else ensure().depth = v;
      clean(); showD();
    });
    depth.addEventListener('change', onEdit);
    row.append(depth, depthLbl);
    // breath period
    const rate = document.createElement('input');
    rate.type = 'number'; rate.min = '0'; rate.max = '120'; rate.step = '0.5';
    rate.value = String(p.mod?.rateSec ?? 6); rate.title = 'breath period (s)';
    Object.assign(rate.style, numStyle);
    rate.addEventListener('input', () => {
      const v = Math.max(0, Number(rate.value) || 0);
      if (v <= 0) { if (p.mod) delete p.mod.rateSec; } else ensure().rateSec = v;
      clean();
    });
    rate.addEventListener('change', onEdit); // DM15
    const rateTag = document.createElement('span'); rateTag.textContent = 's';
    row.append(rate, rateTag);
    // loop-to-loop reshuffle period (needs a oneOf pool to vary)
    const resh = document.createElement('input');
    resh.type = 'number'; resh.min = '0'; resh.max = '600'; resh.step = '1';
    resh.value = String(p.mod?.reshuffleSec ?? 0); resh.title = 'reshuffle the variant every N s (needs a oneOf pool)';
    Object.assign(resh.style, numStyle);
    resh.addEventListener('input', () => {
      const v = Math.max(0, Number(resh.value) || 0);
      if (v <= 0) { if (p.mod) delete p.mod.reshuffleSec; } else ensure().reshuffleSec = v;
      clean();
    });
    resh.addEventListener('change', onEdit); // DM15
    const reshTag = document.createElement('span'); reshTag.textContent = '⟳s';
    row.append(resh, reshTag);
    return row;
  };

  // Bed spatial editor: spatial type + per-source falloff radii. `core`
  // (refDistance) is the full-presence inner radius; `reach` (maxDistance) the
  // audible outer edge — the two wireframe spheres the 3D audio gizmos draw.
  // Empty = inherit the spatial-type preset (shown as the placeholder). Mutates
  // p live: the SoundscapeEngine re-routes the bed so the AUDIBLE falloff
  // updates, and the gizmos resize the spheres on the next frame.
  const makeSpatialEditor = (p: SoundPlacement, onEdit: () => void): HTMLElement => {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', fontSize: '8.5px', color: MUTED });
    row.addEventListener('pointerdown', (e) => e.stopPropagation());
    const tag = document.createElement('span'); tag.textContent = 'space'; row.appendChild(tag);
    const spatialSel = document.createElement('select');
    Object.assign(spatialSel.style, selStyle); spatialSel.style.fontSize = '8.5px';
    for (const s of ['point', 'area', 'global']) { const o = document.createElement('option'); o.value = s; o.textContent = s; spatialSel.appendChild(o); }
    spatialSel.value = p.spatial ?? 'point';
    spatialSel.title = 'spatial falloff type — point (tight) · area (room) · global (everywhere, no sphere)';
    const core = document.createElement('input');
    core.type = 'number'; core.min = '0'; core.step = '0.5';
    core.title = 'core radius (refDistance, m) — full presence within';
    Object.assign(core.style, numStyle);
    const reach = document.createElement('input');
    reach.type = 'number'; reach.min = '0'; reach.step = '1';
    reach.title = 'reach radius (maxDistance, m) — the audible falloff edge';
    Object.assign(reach.style, numStyle);
    const sync = (): void => {
      const isGlobal = spatialSel.value === 'global';
      const preset = spatialFalloff(spatialSel.value as SpatialType) ?? { refDistance: 0, maxDistance: 0 };
      core.value = p.refDistance != null ? String(p.refDistance) : '';
      reach.value = p.maxDistance != null ? String(p.maxDistance) : '';
      core.placeholder = isGlobal ? '—' : String(preset.refDistance);
      reach.placeholder = isGlobal ? '—' : String(preset.maxDistance);
      core.disabled = reach.disabled = isGlobal;
      core.style.opacity = reach.style.opacity = isGlobal ? '0.4' : '1';
    };
    sync();
    spatialSel.addEventListener('change', () => { p.spatial = spatialSel.value as SpatialType; sync(); onEdit(); });
    core.addEventListener('input', () => {
      const v = Number(core.value);
      if (core.value === '' || !(v > 0)) delete p.refDistance; else p.refDistance = v;
      onEdit();
    });
    reach.addEventListener('input', () => {
      const v = Number(reach.value);
      if (reach.value === '' || !(v > 0)) delete p.maxDistance; else p.maxDistance = v;
      onEdit();
    });
    const cTag = document.createElement('span'); cTag.textContent = 'core';
    const rTag = document.createElement('span'); rTag.textContent = 'reach';
    row.append(spatialSel, cTag, core, rTag, reach);
    return row;
  };

  // A lane's authoring "brush" (CUES picker, BEDS picker) collapses to a thin
  // ▾ reveal in the lane's settings reserve; the full brush opens as a popover
  // ABOVE the strip on demand, so the track underneath runs nearly full-width
  // (maximum track, minimal always-on filler). Only one brush popover is open
  // at a time. The reserve keeps the shared SETTINGS_W so every time-axis lane
  // (TIME · CUES · BEDS) stays the exact same width — a cue at t still sits
  // under the TIME playhead at t.
  const openBrushPops: HTMLElement[] = [];
  const makeBrushReveal = (label: string, children: HTMLElement[], hint: string): HTMLElement => {
    const reserve = document.createElement('div');
    Object.assign(reserve.style, { flexShrink: '0', width: SETTINGS_W, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', position: 'relative' });
    const toggle = mkBtn('▾');
    toggle.title = `${label} — pick + place`;
    toggle.style.fontSize = '10px'; toggle.style.padding = '1px 4px'; toggle.style.lineHeight = '1';
    const pop = document.createElement('div');
    Object.assign(pop.style, { position: 'absolute', bottom: 'calc(100% + 8px)', right: '0', zIndex: '40', display: 'none', flexDirection: 'column', gap: '6px', padding: '8px 10px', background: 'rgba(12,15,20,0.97)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '6px', boxShadow: '0 6px 24px rgba(0,0,0,0.45)', minWidth: '210px' });
    const head = document.createElement('div'); head.textContent = label;
    Object.assign(head.style, { color: MUTED, fontSize: '8px', letterSpacing: '0.5px', textTransform: 'uppercase' });
    const hintEl = document.createElement('div'); hintEl.textContent = hint;
    Object.assign(hintEl.style, { color: MUTED, fontSize: '9px' });
    const rowEl = document.createElement('div');
    Object.assign(rowEl.style, { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' });
    rowEl.append(...children);
    pop.append(head, hintEl, rowEl);
    openBrushPops.push(pop);
    toggle.addEventListener('click', () => {
      const show = pop.style.display === 'none';
      for (const p of openBrushPops) p.style.display = 'none';
      pop.style.display = show ? 'flex' : 'none';
      toggle.style.borderColor = show ? ACCENT : 'rgba(255,255,255,0.12)';
    });
    reserve.append(pop, toggle);
    return reserve;
  };

  // ── cues lane (time-of-day scheduled one-shots) ─────────────────────
  // The authored counterpart to the live SOUND lane below: drop a sound at a
  // moment of the day and the scheduler fires it there. Diamonds sit at each
  // cue's t, colored by channel (same language as SOUND + the 3D spheres); a
  // warm/cool ring flags an era-gated cue. The picker on the right is the
  // "brush" — choose a sound, an optional hero to anchor it to, and an era,
  // then click the track to drop. Drag a diamond to move; click to select
  // (✕ deletes); Save (rail · brush · ⌘S) writes soundscape.json. Edits are live
  // — the scheduler reads the array each frame, so a drop is audible immediately;
  // the save buttons turn amber (unsaved) until you persist them to disk.
  const cuesRow = document.createElement('div');
  Object.assign(cuesRow.style, { display: 'flex', alignItems: 'center', gap: '8px', height: '24px' });
  const cuesLbl = document.createElement('div');
  cuesLbl.textContent = 'TRIG'; // N10 — drops a trigger clip (the unified citizen), shown in CLIPS
  Object.assign(cuesLbl.style, { color: MUTED, fontSize: '9px', letterSpacing: '0.5px', width: '36px', flexShrink: '0' });
  const cueTrack = document.createElement('div');
  Object.assign(cueTrack.style, { flex: '1 1 auto', minWidth: '0', position: 'relative', height: '6px', background: TRACK_BG, borderRadius: '3px', cursor: 'copy' });
  // Faint ticks at the TIME lane's snaps so cues read against the day at a glance.
  for (const s of TIME_SNAPS) {
    const tick = document.createElement('div');
    Object.assign(tick.style, { position: 'absolute', left: `${tToFrac(s.t) * 100}%`, top: '-3px', bottom: '-3px', width: '1px', background: TICK_COLOR, pointerEvents: 'none' }); // W3b — window-mapped
    cueTrack.appendChild(tick);
  }
  // Empty-state affordance — the CUES counterpart to the BEDS "arm a loop → +
  // bed" hint, so the drop gesture is discoverable. Shown only while the lane
  // has no cues; hidden once one exists or while hovering (the ghost diamond
  // previews the drop then). Overflows the thin 6px track vertically by design.
  const cueEmpty = document.createElement('div');
  cueEmpty.textContent = '▸ pick a sound, click to drop a trigger (→ CLIPS)';
  Object.assign(cueEmpty.style, { position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: '9px', fontStyle: 'italic', opacity: '0.7', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: '3' });
  cueTrack.appendChild(cueEmpty);
  const assetSel = document.createElement('select');
  Object.assign(assetSel.style, selStyle);
  for (const a of opts.audioAssets) {
    const o = document.createElement('option'); o.value = a.id; o.textContent = prettyAsset(a.id); assetSel.appendChild(o);
  }
  assetSel.value = (opts.audioAssets.find((a) => a.defaultChannel === 'sfx') ?? opts.audioAssets[0])?.id ?? '';
  assetSel.title = 'cue sound to drop (or arm one in the AUDIO palette)';
  assetSel.style.width = '104px';
  // Changing the brush here announces it so the AUDIO palette highlights the
  // same sound — the two pickers are one brush.
  assetSel.addEventListener('change', () => { devBus.emit('audio:arm', { id: assetSel.value }); syncClipArmed(); });
  const anchorSel = document.createElement('select');
  Object.assign(anchorSel.style, selStyle);
  for (const h of ['(none)', ...opts.heroIds]) {
    const o = document.createElement('option'); o.value = h; o.textContent = h === '(none)' ? '⌖ none' : h.replace(/^hero_/, '⌖ '); anchorSel.appendChild(o);
  }
  anchorSel.title = 'anchor the cue to a hero (spatial)';
  anchorSel.style.width = '62px';
  const eraSel = document.createElement('select');
  Object.assign(eraSel.style, selStyle);
  for (const e of ['both', 'past', 'present']) {
    const o = document.createElement('option'); o.value = e; o.textContent = e; eraSel.appendChild(o);
  }
  eraSel.title = 'era this cue fires in';
  eraSel.style.width = '70px';
  const saveCuesBtn = mkSaveBtn();
  // T4 — the standalone TRIG lane is retired: trigger one-shots are created
  // right on the unified CLIPS surface via a sync|trig drop-mode toggle, and
  // already render there as ▶/dotted bars. `cuesRow` is still built (the cue
  // machinery stays dormant — cues[] is empty post-N10) but is no longer mounted
  // into `lanes`, so there's no second lane for one citizen. `assetSel` moves to
  // the CLIPS brush (below); anchor/era are set per-clip in the editor.
  cuesRow.append(cuesLbl, cueTrack, makeBrushReveal('trigger brush', [anchorSel, eraSel, saveCuesBtn], 'retired — drop triggers on the unified CLIPS surface (sync|trig toggle).'));
  // cuesRow intentionally NOT appended to `lanes` (TRIG folded into CLIPS — T4).

  // ── MUSIC (T5) — the slot renders as region bars on the unified surface ───
  // The old thin text strip ("silent · flip → music 5") is retired: the music
  // slot now shows as full-width cyan region bars right in the CLIPS surface
  // (era-scoped), with the live playing/position/tape state on the bars and the
  // details in an on-select editor (see makeMusicRow / refreshMusicBars below).
  // `refreshMusicBars` (defined with the surface) is polled from refresh().

  // ── CLIPS lane (N2) — timeline-aligned REGIONS with real durations ────────
  // Beds are weather, cues are lightning, clips are speech: a clip occupies
  // [t, t+dur] where dur is REAL SECONDS (the day is dayLengthSeconds long —
  // labels show m:ss, tooltips show both axes). Drag a bar to move it; edge
  // grips trim like an NLE (left grip slides the region start while the END
  // stays planted — the source `in` advances to match, so the audible content
  // keeps its place in time; right grip resizes dur). Era language carried
  // over from cues: off-era regions render dimmed with a dashed edge.
  const clipRow = document.createElement('div');
  Object.assign(clipRow.style, { display: 'flex', alignItems: 'flex-start', gap: '8px', position: 'relative' }); // T7 — label/brush top-align so the surface grows downward
  const clipLbl = document.createElement('div');
  clipLbl.textContent = 'CLIPS';
  clipLbl.title = 'the unified clip surface — sync regions (solid) + trigger one-shots (▶/dotted) stacked on the time axis; drop with the sync|trig toggle (TRIG lane folded in here)';
  Object.assign(clipLbl.style, { color: MUTED, fontSize: '9px', letterSpacing: '0.5px', width: '36px', flexShrink: '0' });
  const clipStack = document.createElement('div');
  // T7 — auto-size to content: a thin labeled rail when empty (minHeight), grows
  // with the stacked bars (music + clips), scrolls past a max instead of a fixed
  // band reserving dead space.
  Object.assign(clipStack.style, { flex: '1 1 auto', minWidth: '0', position: 'relative', minHeight: '20px', overflow: 'hidden', background: TRACK_BG, borderRadius: '3px', cursor: 'copy' });
  for (const s of TIME_SNAPS) {
    const tick = document.createElement('div');
    Object.assign(tick.style, { position: 'absolute', left: `${tToFrac(s.t) * 100}%`, top: '0', bottom: '0', width: '1px', background: TICK_COLOR, pointerEvents: 'none', zIndex: '1' }); // W3b — window-mapped
    clipStack.appendChild(tick);
  }
  const clipHeadEl = document.createElement('div');
  Object.assign(clipHeadEl.style, { position: 'absolute', left: '0%', top: '0', bottom: '0', width: '1px', background: ACCENT, opacity: '0.5', pointerEvents: 'none', zIndex: '5' });
  clipStack.appendChild(clipHeadEl);
  const clipEmpty = document.createElement('div');
  clipEmpty.textContent = '▸ pick a sound, click to lay a region';
  Object.assign(clipEmpty.style, { position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: '9px', fontStyle: 'italic', opacity: '0.7', pointerEvents: 'none' });
  clipStack.appendChild(clipEmpty);
  // The brush control strip — a dedicated row at the TOP of the surface holding
  // the drop-mode toggle (left) + the armed-source chip (right). They used to be
  // absolute (top:1px) and floated OVER the first content row, colliding with the
  // music bar's label; on their own flow row the stacked music/clip bars render
  // cleanly beneath, so nothing covers anything.
  const clipHeader = document.createElement('div');
  Object.assign(clipHeader.style, { position: 'relative', zIndex: '6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', height: '14px', padding: '0 6px', boxSizing: 'border-box', background: 'rgba(18,22,28,0.55)', borderBottom: '1px solid rgba(255,255,255,0.07)' });
  clipStack.appendChild(clipHeader);
  // T2 — the armed source, legible AT the CLIPS surface. The selector itself
  // lives in the TRIG brush (another lane), so a click here used to drop an
  // unseen sound. This always-visible chip names what a click will lay; a
  // dead-click (nothing armed) pulses it with a "pick a sound" prompt instead
  // of silently doing nothing.
  const clipArmed = document.createElement('div');
  Object.assign(clipArmed.style, { fontSize: '8px', pointerEvents: 'none', opacity: '0.85', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: '1', minWidth: '0', textAlign: 'right' });
  const syncClipArmed = (): void => {
    const id = assetSel.value;
    if (id) {
      clipArmed.textContent = `▸ ${prettyAsset(id)}`;
      clipArmed.style.color = channelCssColor(opts.audioAssets.find((a) => a.id === id)?.defaultChannel ?? 'narration');
    } else {
      clipArmed.textContent = '▸ pick a sound';
      clipArmed.style.color = MUTED;
    }
  };
  const flashPickASound = (): void => {
    clipArmed.textContent = '▸ pick a sound in the AUDIO palette';
    clipArmed.style.color = '#ffd87c';
    clipArmed.animate([{ opacity: '1' }, { opacity: '0.35' }, { opacity: '1' }], { duration: 850, iterations: 2 });
    setTimeout(syncClipArmed, 1800); // restore once the nudge has registered
  };
  // T4 — drop-mode: a click on the surface lays a `sync` region (clock-locked)
  // or a `trigger` one-shot (the former TRIG drop, folded in). Always-visible
  // top-left so what a drop creates is legible next to the armed-source chip.
  let dropMode: 'sync' | 'trigger' = 'sync';
  const clipMode = document.createElement('div');
  Object.assign(clipMode.style, { display: 'flex', gap: '3px', alignItems: 'center', flexShrink: '0' });
  const modeCap = document.createElement('span');
  modeCap.textContent = 'drop';
  modeCap.title = 'what a click on the surface lays: a SYNC region (plays locked to the day clock) or a TRIG one-shot (fires once when the playhead crosses it)';
  Object.assign(modeCap.style, { color: MUTED, fontSize: '8px', letterSpacing: '0.3px' });
  clipMode.appendChild(modeCap);
  const modeBtns: Partial<Record<'sync' | 'trigger', HTMLSpanElement>> = {};
  const syncModeBtns = (): void => {
    (['sync', 'trigger'] as const).forEach((m) => {
      const b = modeBtns[m]; if (!b) return; const on = dropMode === m;
      b.style.background = on ? channelCssColor('sfx') : 'transparent';
      b.style.color = on ? '#0e1218' : MUTED;
      b.style.borderColor = on ? channelCssColor('sfx') : 'rgba(255,255,255,0.18)';
    });
  };
  (['sync', 'trigger'] as const).forEach((m) => {
    const b = document.createElement('span');
    b.textContent = m === 'sync' ? 'sync' : '▶ trig';
    b.title = m === 'sync' ? 'a drop lays a clock-locked region' : 'a drop lays a one-shot trigger (fires once on crossing)';
    Object.assign(b.style, { fontSize: '8px', padding: '0 4px', borderRadius: '3px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.18)', lineHeight: '12px' });
    b.addEventListener('pointerdown', (e) => e.stopPropagation()); // not a surface create
    b.addEventListener('click', (e) => { e.stopPropagation(); dropMode = m; syncModeBtns(); });
    modeBtns[m] = b; clipMode.appendChild(b);
  });
  // Assemble the control strip: drop-mode toggle (left) + armed-source chip (right).
  clipHeader.append(clipMode, clipArmed);
  syncModeBtns();
  const saveClipsBtn = mkSaveBtn();
  clipRow.append(clipLbl, clipStack, makeBrushReveal('clips brush', [assetSel, saveClipsBtn], 'pick a sound (or arm one in the AUDIO palette) + the sync|trig mode, then click the surface to lay it. drag moves; edge grips trim; ⌘S saves.'));
  lanes.appendChild(clipRow);

  let selectedClipId: string | null = null;
  // T5 — music slots render as full-width region bars on this same surface
  // (era-scoped, cyan), so the music slot reads as a clip from the start instead
  // of a text strip. One selection model with clips (cleared together).
  let selectedMusicId: string | null = null;
  const musicRowEls = new Map<string, HTMLElement>();
  // T6 ⚑ (open sky) — beds can render as a grouped cluster ON this unified surface
  // (one stacked area) instead of their own band. Presentation only — the bed
  // engine is untouched. Reversible: the "beds ⤢" toggle (or __lec.timeline) flips
  // `bedsOnSurface` and the beds return to the separate BEDS band. Default OFF (the
  // proven band); John decides by eye whether the merged read is clearer.
  let bedsOnSurface = false;
  let bedsCollapsed = false;
  // T3 — clip ids whose `dur` is an unconfirmed placeholder: laid before the
  // source sample decoded, so its real length wasn't known yet. The bar shows
  // "len?" while pending; an `audio:loaded` corrects + clears it, as does a
  // manual dur edit/resize (the user confirmed a length). Transient (not saved).
  const pendingLen = new Set<string>();
  // clip id → its BAR element (the focus citizen). Clips are GROUPED by sound:
  // every clip of the same asset shares one row (packed into as few sub-lanes as
  // overlap requires), so the same sound reads as one conserved band instead of
  // sprawling one-row-per-clip down the surface. clipGroupRows holds the row
  // containers for teardown (several bars map to one row).
  const clipRowEls = new Map<string, HTMLElement>();
  const clipGroupRows: HTMLElement[] = [];
  const CLIP_ROW_H = 16;
  const MIN_CLIP_DUR = 1; // seconds — a region can't trim away entirely
  const daySecs = (): number => Math.max(1, opts.clock.dayLengthSeconds);
  const fmtMMSS = (s: number): string => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const clipT = (clientX: number): number => {
    const rect = clipStack.getBoundingClientRect();
    return fracToT(Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)))); // W3b — lane frac → windowed t
  };

  // One clip's BAR (no row/line wrapper — repaintClipBars groups bars of the same
  // sound onto a shared line). Returns the bar; it carries its own focus binding,
  // drag/trim handlers, and delete affordance exactly as before.
  const makeClipBar = (p: SoundPlacement): HTMLElement => {
    const color = channelCssColor(p.channel ?? 'narration');
    const selected = selectedClipId === p.id;
    const era = displayEra();
    const onEra = !p.scope || p.scope === 'both' || p.scope === era;
    const durDay = (p.dur ?? 0) / daySecs();
    // N10 — one citizen, two looks. A `trigger` clip (the former cue) edge-fires
    // and plays fire-and-forget: lighter body + a ▶ ignition mark so it reads as
    // a one-shot, not a clock-locked region. `sync` clips stay solid.
    const isTrigger = p.playback === 'trigger';
    const name = (p as unknown as { label?: string }).label || prettyAsset(p.asset ?? '?');
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      pointerEvents: 'auto', // parent row/line are pe:none (so empty clicks reach the stack → add); the bar itself stays interactive
      position: 'absolute', left: `${tToFrac(p.t ?? 0) * 100}%`, width: `${Math.max(0.3, (durDay / daySpan()) * 100)}%`, // W3b — window-mapped
      top: '1px', bottom: '1px', minWidth: '6px',
      background: color + (selected ? '4d' : (isTrigger ? '14' : '22')),
      border: `1px ${onEra ? (isTrigger ? 'dotted' : 'solid') : 'dashed'} ${color}${selected ? '' : '99'}`,
      borderRadius: '2px', boxSizing: 'border-box', cursor: 'grab', touchAction: 'none', overflow: 'hidden',
      opacity: onEra ? '1' : '0.45',
    });
    const lbl = document.createElement('span');
    const trim = (p.in ?? 0) > 0 || p.out !== undefined ? ' ✂' : '';
    const durLbl = pendingLen.has(p.id) ? 'len?' : fmtMMSS(p.dur ?? 0); // T3 — length unknown until the source decodes
    lbl.textContent = `${isTrigger ? '▶ ' : ''}${name} · ${durLbl}${trim}`;
    Object.assign(lbl.style, { position: 'absolute', left: '4px', top: '0', fontSize: '8px', lineHeight: `${CLIP_ROW_H - 2}px`, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', pointerEvents: 'none', opacity: '0.92' });
    bar.title = `${name} · ${isTrigger ? 'TRIGGER (fires once on crossing)' : 'SYNC (clock-locked region)'} · ${(p.dur ?? 0).toFixed(1)}s (${fmtMMSS(p.dur ?? 0)}) at ${tHM(p.t ?? 0)}${isTrigger ? '' : ` · in ${(p.in ?? 0).toFixed(1)}s${p.out !== undefined ? ` out ${p.out.toFixed(1)}s` : ''}`} · ${p.scope ?? 'both'}${onEra ? '' : ' (other era — dimmed)'}`;
    // W2 — audible extent vs region. Now that W1 hard-caps the source at `out`
    // (or the decoded asset length), any region time past where the source runs
    // out plays SILENCE. Shade that tail so what reads solid = what you hear
    // (the NLE source-vs-region convention). assetDuration is undefined until
    // the sample decodes (T3 shows "len?"), so no shade is drawn until known.
    const assetDur = opts.audio.assetDuration(p.asset ?? '');
    const regionLen = p.dur ?? 0;
    const audibleLen = Math.max(0, Math.min(regionLen, (p.out ?? assetDur ?? Infinity) - (p.in ?? 0)));
    if (regionLen > 0 && audibleLen < regionLen - 1e-4) {
      const shade = document.createElement('div');
      Object.assign(shade.style, {
        position: 'absolute', left: `${(audibleLen / regionLen) * 100}%`, right: '0', top: '0', bottom: '0',
        background: 'rgba(8,10,14,0.55)', borderLeft: `1px dashed ${color}aa`,
        pointerEvents: 'none', borderRadius: '0 2px 2px 0',
      });
      shade.title = 'silent tail — the source ends before the region does (out-point / sample length)';
      bar.appendChild(shade);
    }
    const mkGrip = (side: 'left' | 'right'): HTMLDivElement => {
      const g = document.createElement('div');
      Object.assign(g.style, { position: 'absolute', top: '0', bottom: '0', [side]: '0', width: '5px', cursor: 'ew-resize' });
      return g;
    };
    const gripL = mkGrip('left'); const gripR = mkGrip('right');
    const del = document.createElement('span');
    del.textContent = '✕';
    Object.assign(del.style, { position: 'absolute', right: '7px', top: '0', fontSize: '9px', lineHeight: `${CLIP_ROW_H - 2}px`, color: '#ff9a9a', cursor: 'pointer', opacity: '0', zIndex: '4', padding: '0 2px' });
    del.addEventListener('pointerdown', (e) => { e.stopPropagation(); e.preventDefault(); removeClip(p.id); });
    const ccand: FocusRef = { placementId: p.id, assetId: p.asset, channel: p.channel ?? 'narration', ...(p.at?.heroId ? { heroId: p.at.heroId } : {}) };
    bar.addEventListener('mouseenter', () => { del.style.opacity = '1'; emitFocus(ccand); });
    bar.addEventListener('mouseleave', () => { del.style.opacity = '0'; emitFocus(null); });
    bar.append(lbl, gripL, gripR, del);
    // ── bar drag: move along the time axis; click selects/deselects ──
    let dragging = false, moved = false, downX = 0, startT = 0;
    bar.addEventListener('pointerdown', (e) => {
      if (e.target === gripL || e.target === gripR || e.target === del) return;
      e.stopPropagation(); e.preventDefault();
      dragging = true; moved = false; downX = e.clientX; startT = p.t ?? 0;
      beginDrag();
      try { bar.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      bar.style.cursor = 'grabbing';
    });
    bar.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (!moved && Math.abs(e.clientX - downX) > 3) { moved = true; flagDirtyDuringDrag(); }
      if (moved) {
        const rect = clipStack.getBoundingClientRect();
        const dtT = ((e.clientX - downX) / Math.max(1, rect.width)) * daySpan(); // W3b — lane-frac delta → t delta
        const nt = Math.max(opts.clock.dayStart, Math.min(opts.clock.dayEnd - durDay, startT + dtT));
        p.t = nt;
        bar.style.left = `${tToFrac(nt) * 100}%`;
      }
    });
    const up = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false; endDrag(); bar.style.cursor = 'grab';
      try { bar.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      if (moved) { markDirty(); renderClips(); }
      else {
        const willSelect = selectedClipId !== p.id;
        selectedClipId = willSelect ? p.id : null;
        selectedMusicId = null; selectedBedId = null; // T8 — one selection at a time across citizens
        if (p.at?.heroId) devBus.emit('hero:select', { id: willSelect ? p.at.heroId : null });
        renderClips();
        renderAmbience(); // hide a now-stale bed editor
      }
    };
    bar.addEventListener('pointerup', up);
    bar.addEventListener('pointercancel', up);
    // ── edge grips: NLE trim ──
    const wireClipGrip = (grip: HTMLElement, side: 'L' | 'R'): void => {
      let active = false, gmoved = false, gStartT = 0, gStartDur = 0, gStartIn = 0;
      grip.addEventListener('pointerdown', (e) => {
        e.stopPropagation(); e.preventDefault(); active = true; gmoved = false;
        gStartT = p.t ?? 0; gStartDur = p.dur ?? 0; gStartIn = p.in ?? 0;
        beginDrag();
        try { grip.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      });
      grip.addEventListener('pointermove', (e) => {
        if (!active) return;
        if (!gmoved) { gmoved = true; flagDirtyDuringDrag(); }
        const pointerSec = clipT(e.clientX) * daySecs();
        if (side === 'L') {
          // Slide the region start; the END stays planted; `in` follows the
          // delta so the audible content holds its place on the timeline.
          const endSec = gStartT * daySecs() + gStartDur;
          let deltaSec = pointerSec - gStartT * daySecs();
          deltaSec = Math.max(-gStartIn, Math.min(gStartDur - MIN_CLIP_DUR, deltaSec));
          p.t = (gStartT * daySecs() + deltaSec) / daySecs();
          p.dur = endSec - (gStartT * daySecs() + deltaSec);
          const ni = gStartIn + deltaSec;
          if (ni > 0.0005) p.in = ni; else delete p.in;
        } else {
          p.dur = Math.max(MIN_CLIP_DUR, pointerSec - (p.t ?? 0) * daySecs());
        }
        pendingLen.delete(p.id); // T3 — a manual resize confirms the length
        bar.style.left = `${tToFrac(p.t ?? 0) * 100}%`;
        bar.style.width = `${Math.max(0.3, ((p.dur ?? 0) / daySecs() / daySpan()) * 100)}%`; // W3b — window-mapped
        lbl.textContent = `${prettyAsset(p.asset ?? '?')} · ${fmtMMSS(p.dur ?? 0)}${(p.in ?? 0) > 0 || p.out !== undefined ? ' ✂' : ''}`;
      });
      const gup = (e: PointerEvent): void => {
        if (!active) return; active = false; endDrag();
        try { grip.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if (gmoved) { markDirty(); renderClips(); }
      };
      grip.addEventListener('pointerup', gup);
      grip.addEventListener('pointercancel', gup);
    };
    wireClipGrip(gripL, 'L'); wireClipGrip(gripR, 'R');
    bindFocus(bar, ccand, p.channel ?? 'narration', bar);
    return bar;
  };

  // Greedy interval-pack a sound's clips into the fewest sub-lanes so overlapping
  // bars of the SAME sound never stack illegibly: each clip drops into the first
  // sub-lane whose last bar has ended by its start, else opens a new one. The
  // common case (clips that don't overlap in time) collapses to a single lane —
  // one conserved row for that sound.
  const packClipLanes = (items: SoundPlacement[]): SoundPlacement[][] => {
    const sorted = [...items].sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
    const lanes: Array<{ end: number; items: SoundPlacement[] }> = [];
    for (const p of sorted) {
      const start = p.t ?? 0;
      const end = start + Math.max(0, p.dur ?? 0) / daySecs();
      const lane = lanes.find((l) => start >= l.end - 1e-4);
      if (lane) { lane.items.push(p); lane.end = end; }
      else lanes.push({ end, items: [p] });
    }
    return lanes.map((l) => l.items);
  };

  const removeClip = (id: string): void => {
    const i = opts.placements.findIndex((x) => x.id === id);
    if (i >= 0) { opts.placements.splice(i, 1); markDirty(); }
    if (selectedClipId === id) selectedClipId = null;
    renderClips();
  };
  const addClipAt = (t: number): void => {
    const assetId = assetSel.value;
    if (!assetId) { flashPickASound(); return; } // T2 — never a silent no-op
    const a = opts.audioAssets.find((x) => x.id === assetId);
    // Default region length: the real source length (clamped sane) so a laid
    // region matches what it will actually say; synths default to 8s.
    const srcLen = opts.audio.assetDuration(assetId);
    const dur = Math.max(2, Math.min(30, srcLen ?? 8));
    const clip: SoundPlacement = {
      id: 'clip_' + Math.random().toString(36).slice(2, 9),
      kind: 'clip', asset: assetId, scope: displayEra(),
      channel: a?.defaultChannel === 'music' ? 'narration' : (a?.defaultChannel ?? 'narration'),
      t: Math.max(0, Math.min(1 - dur / daySecs(), t)), dur,
      // T4 — the surface drop-mode picks the citizen's look: a `trigger` one-shot
      // (▶/dotted, fires on crossing) or the default `sync` region (solid).
      ...(dropMode === 'trigger' ? { playback: 'trigger' as const } : {}),
    };
    opts.placements.push(clip);
    // T3 — an undecoded SAMPLE has no known length yet (synths report loaded),
    // so mark the clip pending: its bar shows "len?" until audio:loaded corrects
    // the placeholder dur. (Without this it froze at the 8s default forever.)
    if (!opts.audio.isLoaded(assetId)) pendingLen.add(clip.id);
    selectedClipId = clip.id;
    markDirty();
    renderClips();
  };
  // ── clip editor overlay (N3) — every aspect of a region in one popover,
  //    floating above the strip so the lane never grows or scrolls (the bed
  //    editor pattern). Rebuilt on selection; numeric edits re-render the bar.
  const clipEditorPop = document.createElement('div');
  Object.assign(clipEditorPop.style, {
    position: 'absolute', bottom: 'calc(100% + 8px)', left: '8px', zIndex: '40',
    display: 'none', flexDirection: 'column', gap: '3px', padding: '7px 9px',
    background: 'rgba(12,15,20,0.97)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '6px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.45)', minWidth: '180px',
  });
  clipRow.appendChild(clipEditorPop);
  clipEditorPop.addEventListener('pointerdown', (e) => e.stopPropagation());
  // T1 — which clip the editor's INPUTS are currently built for. A field commit,
  // or an unrelated re-render (drag-end, era flip, re-selecting the same clip),
  // must NOT rebuild the inputs for an unchanged selection — rebuilding is what
  // dropped focus to <body> and flashed the popover on every edit. We rebuild
  // only when the selected clip actually changes.
  let clipEditorFor: string | null = null;
  let clipEditorTitle: HTMLSpanElement | null = null;
  const hideClipEditor = (): void => { clipEditorPop.style.display = 'none'; clipEditorFor = null; };
  // Lightweight refresh of the OPEN editor's chrome (title + border + position)
  // without touching its inputs — keeps focus + avoids the flash after a commit.
  const refreshClipEditorMeta = (p: SoundPlacement): void => {
    if (clipEditorFor !== p.id) return;
    clipEditorPop.style.borderColor = channelCssColor(p.channel ?? 'narration');
    if (clipEditorTitle) clipEditorTitle.textContent = `${prettyAsset(p.asset ?? '?')} · ${fmtMMSS(p.dur ?? 0)}`;
    const rowRect = clipRow.getBoundingClientRect();
    const barLeft = (p.t ?? 0) * (rowRect.width - 120);
    clipEditorPop.style.left = `${Math.max(0, Math.min(barLeft, rowRect.width - 200))}px`;
  };
  const buildClipEditor = (p: SoundPlacement): void => {
    clipEditorPop.textContent = '';
    const color = channelCssColor(p.channel ?? 'narration');
    clipEditorPop.style.borderColor = color;
    const head = document.createElement('div');
    Object.assign(head.style, { display: 'flex', alignItems: 'center', gap: '6px' });
    const title = document.createElement('span');
    title.textContent = `${prettyAsset(p.asset ?? '?')} · ${fmtMMSS(p.dur ?? 0)}`;
    Object.assign(title.style, { fontSize: '9px', color: TEXT, flex: '1 1 auto' });
    clipEditorTitle = title;
    const del = document.createElement('span');
    del.textContent = '✕';
    Object.assign(del.style, { fontSize: '10px', color: '#ff8a8a', cursor: 'pointer' });
    del.addEventListener('pointerdown', (e) => { e.stopPropagation(); e.preventDefault(); removeClip(p.id); });
    head.append(title, del);
    // T1 — a field commit repaints the BAR (size/position/colour follow the
    // data) and refreshes the editor's chrome, but never rebuilds the inputs —
    // so focus stays put and Tab moves to the next field. markDirty fires once
    // per real edit (undo stays one-step-per-edit; repaint doesn't touch undo).
    const commit = (): void => { markDirty(); repaintClipBars(); refreshClipEditorMeta(p); };
    const fields = document.createElement('div');
    Object.assign(fields.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
    fields.append(
      edRow('snd', edSel(assetEntriesFor(p.channel ?? 'narration', p.asset), p.asset ?? '', 'swap the source (⌁ = synth patch — generated to length, trim ignored)', (v) => { p.asset = v; commit(); })),
      // N10 — the cue/clip merge knob: sync = clock-locked region; trigger =
      // edge-fired one-shot (former cue). Flipping it re-renders the bar's look.
      edRow('mode', edSel([{ v: 'sync', label: 'sync · region' }, { v: 'trigger', label: 'trigger · one-shot' }], p.playback ?? 'sync', 'sync = plays while the playhead is inside the region (scrub-seeks, stops on leave); trigger = fires once when the playhead crosses the start, then plays out', (v) => { if (v === 'trigger') p.playback = 'trigger'; else delete p.playback; commit(); })),
      edRow('t', edNum(p.t, 0.005, 'region start, day units 0..1', (v) => { p.t = Math.max(0, Math.min(1, v)); commit(); })),
      edRow('dur', edNum(p.dur, 0.5, 'region length in REAL seconds', (v) => { p.dur = Math.max(MIN_CLIP_DUR, v); pendingLen.delete(p.id); commit(); })), // T3 — manual length confirms it
      edRow('in', edNum(p.in, 0.1, 'source in-point (seconds); 0 clears', (v) => { if (v > 0) p.in = v; else delete p.in; commit(); })),
      edRow('out', edNum(p.out, 0.1, 'source out-point (seconds); 0 clears (play to end)', (v) => { if (v > (p.in ?? 0)) p.out = v; else delete p.out; commit(); })),
      // W2 — author the W1 envelope fades from the UI (not hand-edited JSON).
      // Blank/0 clears → the engine's DEFAULT_FADE (~0.06s) applies.
      edRow('fade in', edNum(p.fadeIn, 0.01, 'fade-in seconds; blank/0 = the default (~0.06s)', (v) => { if (v > 0) p.fadeIn = v; else delete p.fadeIn; commit(); }, 'def')),
      edRow('fade out', edNum(p.fadeOut, 0.01, 'fade-out seconds; blank/0 = the default (~0.06s)', (v) => { if (v > 0) p.fadeOut = v; else delete p.fadeOut; commit(); }, 'def')),
      edRow('vol', edNum(p.gain ?? 1, 0.05, 'gain 0..1', (v) => { p.gain = Math.max(0, Math.min(1, v)); commit(); })),
      edRow('era', edSel([{ v: 'both', label: 'both' }, { v: 'past', label: 'past' }, { v: 'present', label: 'present' }], p.scope ?? 'both', 'era gate (binary at region start)', (v) => { p.scope = v as StateTag; commit(); })),
      edRow('ch', edSel(['ambient', 'music', 'narration', 'sfx', 'room'].map((c) => ({ v: c, label: c })), p.channel ?? 'narration', 'channel', (v) => { p.channel = v as AudioChannel; commit(); })),
      // DM9 — N7 arbitration was schema-only (hand-edit soundscape.json). Surface
      // it: how this scripted clip yields when a visitor's cassette is riding the
      // music slot. Unset behaves as 'force' (the engine default), so the select
      // shows 'force' for an unset clip and writes an explicit value on change.
      edRow('vs 🎚', edSel([
        { v: 'force', label: 'force · play over the tape' },
        { v: 'duck', label: 'duck · play + dip the tape' },
        { v: 'defer', label: 'defer · yield while tape rides' },
        { v: 'skip', label: 'skip · drop while tape rides' },
      ], p.arbitration ?? 'force', 'N7 — when a visitor cassette rides the music slot: force = play over it; duck = play but dip the tape; defer/skip = hold this clip while the tape rides (defer waits for a gap, skip drops the crossing)', (v) => { p.arbitration = v as NonNullable<SoundPlacement['arbitration']>; commit(); })),
      edRow('⌖', edSel([{ v: '', label: '⌖ none' }, ...opts.heroIds.map((h) => ({ v: h, label: h.replace(/^hero_/, '⌖ ') }))], p.at?.heroId ?? '', 'anchor (spatialize at a hero)', (v) => { p.at = v ? { heroId: v } : undefined; commit(); })),
    );
    if (opts.auditionClip) {
      const aud = mkBtn('▸ audition slice');
      aud.style.fontSize = '9px';
      aud.title = 'play the trimmed slice through the real chain at its anchor (click again to stop)';
      aud.addEventListener('pointerdown', (e) => e.stopPropagation());
      aud.addEventListener('click', (e) => { e.stopPropagation(); opts.auditionClip!(p); });
      fields.appendChild(aud);
    }
    // N9: a synth-backed clip grows the patch instrument.
    const patchSec = makePatchSection(p.asset, (newId) => { p.asset = newId; commit(); });
    clipEditorPop.append(head, fields);
    if (patchSec) clipEditorPop.appendChild(patchSec);
    // Park the popover near the bar, clamped inside the row.
    const rowRect = clipRow.getBoundingClientRect();
    const barLeft = (p.t ?? 0) * (rowRect.width - 120);
    clipEditorPop.style.left = `${Math.max(0, Math.min(barLeft, rowRect.width - 200))}px`;
    clipEditorPop.style.display = 'flex';
    clipEditorFor = p.id;
  };

  // Reconcile the editor to the selection: rebuild its inputs ONLY when the
  // selected clip changed (or it's freshly opening). For an unchanged, already-
  // open selection just refresh the chrome — so a bar repaint (commit, drag-end,
  // era flip) can't tear the editor down under the user's hands (T1).
  const showClipEditor = (p: SoundPlacement): void => {
    if (clipEditorFor === p.id && clipEditorPop.style.display !== 'none') refreshClipEditorMeta(p);
    else buildClipEditor(p);
  };

  // Repaint just the clip BARS — never the editor. Safe to call mid-edit: the
  // floating editor's inputs aren't touched, so focus survives (T1).
  // ── T5 — music as a region ───────────────────────────────────────────────
  // The music SLOT (era-default track + a visitor's overriding tape) renders as
  // full-width region bars on this same unified surface — cyan, era-scoped
  // (off-era dimmed + dashed, the clip language). Music has no clock t/dur (it's
  // the slot for its era), so each bar spans the full day. The live slot state
  // (playing, position, the riding tape) shows on the bars; resume/era/asset move
  // into an on-select editor. The old text strip is retired.
  type MusicRowEl = HTMLElement & { _mId?: string; _mLbl?: HTMLSpanElement };
  const musicLabel = (p: SoundPlacement, onEra: boolean, live: boolean, posSec: number): string =>
    `${live ? '▶ ' : ''}♪ ${prettyAsset(p.asset ?? '?')}${p.requiresPower ? ' · ⏚' : ''} · ${p.resume ?? 'restart'}${live ? ` · ${fmtMMSS(posSec)}` : ''}${onEra ? '' : ' · flip → here'}`;
  const musicEditorPop = document.createElement('div');
  Object.assign(musicEditorPop.style, {
    position: 'absolute', bottom: 'calc(100% + 8px)', left: '8px', zIndex: '40',
    display: 'none', flexDirection: 'column', gap: '3px', padding: '7px 9px',
    background: 'rgba(12,15,20,0.97)', border: `1px solid ${channelCssColor('music')}`, borderRadius: '6px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.45)', minWidth: '180px',
  });
  clipRow.appendChild(musicEditorPop);
  musicEditorPop.addEventListener('pointerdown', (e) => e.stopPropagation());
  let musicEditorFor: string | null = null;
  const hideMusicEditor = (): void => { musicEditorPop.style.display = 'none'; musicEditorFor = null; };
  const musicAssetEntries = (): Array<{ v: string; label: string }> =>
    opts.audioAssets.filter((a) => a.defaultChannel === 'music' || a.loopable).map((a) => ({ v: a.id, label: prettyAsset(a.id) }));
  const buildMusicEditor = (p: SoundPlacement): void => {
    musicEditorPop.textContent = '';
    const head = document.createElement('div');
    Object.assign(head.style, { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', color: TEXT });
    const title = document.createElement('span');
    title.textContent = `♪ slot · ${p.scope ?? 'both'}`;
    title.style.flex = '1 1 auto';
    head.appendChild(title);
    const commit = (): void => { markDirty(); repaintClipBars(); };
    const fields = document.createElement('div');
    Object.assign(fields.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
    fields.append(
      edRow('snd', edSel(musicAssetEntries(), p.asset ?? '', "this era's music-slot track", (v) => { p.asset = v; commit(); })),
      edRow('era', edSel([{ v: 'both', label: 'both' }, { v: 'past', label: 'past' }, { v: 'present', label: 'present' }], p.scope ?? 'both', 'which era this slot fills', (v) => { p.scope = v as StateTag; commit(); })),
      edRow('resume', edSel([{ v: 'restart', label: 'restart' }, { v: 'continue', label: 'continue' }], p.resume ?? 'restart', 'on era return: restart the track or continue where it left off', (v) => { if (v === 'continue') p.resume = 'continue'; else p.resume = 'restart'; commit(); })),
      edRow('vol', edNum(p.gain ?? 1, 0.05, 'gain 0..1', (v) => { p.gain = Math.max(0, Math.min(1, v)); commit(); })),
    );
    if (opts.auditionClip) {
      // T8 — same audition affordance as clips/beds: one grammar across citizens.
      const aud = mkBtn('▸ audition');
      aud.style.fontSize = '9px';
      aud.title = 'play the slot track through the real chain at its anchor (click again to stop)';
      aud.addEventListener('pointerdown', (e) => e.stopPropagation());
      aud.addEventListener('click', (e) => { e.stopPropagation(); opts.auditionClip!(p); });
      fields.appendChild(aud);
    }
    musicEditorPop.append(head, fields);
    musicEditorPop.style.display = 'flex';
    musicEditorFor = p.id;
  };
  const showMusicEditor = (p: SoundPlacement): void => {
    if (musicEditorFor === p.id && musicEditorPop.style.display !== 'none') return; // unchanged — keep it
    buildMusicEditor(p);
  };
  // The overriding tape (a visitor cassette riding the slot) — a region over the
  // era defaults, shown only while one rides. Persistent element; refreshed live.
  const musicOverrideRow = document.createElement('div');
  Object.assign(musicOverrideRow.style, { position: 'relative', width: '100%', flexShrink: '0', height: `${CLIP_ROW_H}px`, display: 'none', zIndex: '2', pointerEvents: 'none' });
  const musicOverrideBar = document.createElement('div');
  Object.assign(musicOverrideBar.style, { position: 'absolute', left: '0%', width: '100%', top: '1px', bottom: '1px', background: channelCssColor('music') + '3a', border: `1px solid ${channelCssColor('music')}`, borderRadius: '2px', boxSizing: 'border-box', overflow: 'hidden' });
  const musicOverrideLbl = document.createElement('span');
  Object.assign(musicOverrideLbl.style, { position: 'absolute', left: '5px', top: '0', fontSize: '8px', lineHeight: `${CLIP_ROW_H - 2}px`, color: TEXT, whiteSpace: 'nowrap', pointerEvents: 'none' });
  musicOverrideBar.appendChild(musicOverrideLbl);
  musicOverrideRow.appendChild(musicOverrideBar);
  const makeMusicRow = (p: SoundPlacement): HTMLElement => {
    const color = channelCssColor('music');
    const selected = selectedMusicId === p.id;
    const onEra = !p.scope || p.scope === 'both' || p.scope === displayEra();
    const row = document.createElement('div') as MusicRowEl;
    Object.assign(row.style, { position: 'relative', width: '100%', flexShrink: '0', zIndex: '2', pointerEvents: 'none' });
    const line = document.createElement('div');
    Object.assign(line.style, { position: 'relative', height: `${CLIP_ROW_H}px`, width: '100%' });
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      pointerEvents: 'auto', // parent row/line are pe:none; the music bar stays clickable (selects the slot)
      position: 'absolute', left: '0%', width: '100%', top: '1px', bottom: '1px',
      background: color + (selected ? '4d' : '1c'),
      border: `1px ${onEra ? 'solid' : 'dashed'} ${color}${selected ? '' : '99'}`,
      borderRadius: '2px', boxSizing: 'border-box', cursor: 'pointer', overflow: 'hidden', opacity: onEra ? '1' : '0.5',
    });
    const lbl = document.createElement('span');
    Object.assign(lbl.style, { position: 'absolute', left: '5px', top: '0', fontSize: '8px', lineHeight: `${CLIP_ROW_H - 2}px`, color: TEXT, whiteSpace: 'nowrap', pointerEvents: 'none', opacity: '0.92' });
    lbl.textContent = musicLabel(p, onEra, false, 0);
    bar.title = `music slot · ${p.scope ?? 'both'} era · resume ${p.resume ?? 'restart'} · spans the era (no clock window)`;
    bar.appendChild(lbl);
    bar.addEventListener('click', (e) => {
      e.stopPropagation();
      const willSel = selectedMusicId !== p.id;
      selectedMusicId = willSel ? p.id : null;
      selectedClipId = null; selectedBedId = null; // T8 — one selection at a time across citizens
      if (p.at?.heroId) devBus.emit('hero:select', { id: willSel ? p.at.heroId : null });
      renderClips();
      renderAmbience(); // hide a now-stale bed editor
    });
    line.appendChild(bar);
    row.appendChild(line);
    row._mId = p.id; row._mLbl = lbl;
    return row;
  };
  // Live state on the music bars + the overriding-tape bar (write-on-change).
  const refreshMusicBars = (): void => {
    if (!opts.musicStatus) return;
    const s = opts.musicStatus();
    const era = displayEra();
    for (const row of musicRowEls.values()) {
      const r = row as MusicRowEl; const lbl = r._mLbl; if (!lbl) continue;
      const p = opts.placements.find((x) => x.id === r._mId); if (!p) continue;
      const onEra = !p.scope || p.scope === 'both' || p.scope === era;
      const live = !s.userSelected && onEra && s.assetId === p.asset;
      const want = musicLabel(p, onEra, live, s.posSec);
      if (lbl.textContent !== want) lbl.textContent = want;
    }
    const showOv = s.userSelected && !!s.assetId;
    if ((musicOverrideRow.style.display !== 'none') !== showOv) musicOverrideRow.style.display = showOv ? 'block' : 'none';
    if (showOv) {
      const want = `⏵ tape ${prettyAsset(s.assetId as string)} · ${fmtMMSS(s.posSec)} · rides era flips`;
      if (musicOverrideLbl.textContent !== want) musicOverrideLbl.textContent = want;
    }
  };

  function repaintClipBars(): void {
    for (const el of clipGroupRows) el.remove();
    clipGroupRows.length = 0;
    clipRowEls.clear();
    for (const el of musicRowEls.values()) el.remove();
    musicRowEls.clear();
    musicOverrideRow.remove();
    // T5 — the music slot bars first (the era backdrop), then the riding tape,
    // then the clip bars (speech) on top. All on the one stacked surface.
    for (const p of opts.placements) {
      if (p.kind !== 'music') continue;
      const r = makeMusicRow(p);
      musicRowEls.set(p.id, r);
      clipStack.appendChild(r);
    }
    clipStack.appendChild(musicOverrideRow);
    // Clips GROUPED by sound (asset): one row per sound, its clips packed into as
    // few sub-lanes as time-overlap demands. The same sound therefore reads as a
    // single conserved band — no more one-row-per-clip sprawl down the surface.
    const clips = opts.placements.filter((p) => p.kind === 'clip');
    const groups = new Map<string, SoundPlacement[]>(); // asset → its clips (first-seen order)
    for (const p of clips) {
      const key = p.asset ?? '?';
      const g = groups.get(key);
      if (g) g.push(p); else groups.set(key, [p]);
    }
    for (const members of groups.values()) {
      const row = document.createElement('div');
      Object.assign(row.style, { position: 'relative', width: '100%', flexShrink: '0', zIndex: '2', pointerEvents: 'none' });
      for (const lane of packClipLanes(members)) {
        const line = document.createElement('div');
        Object.assign(line.style, { position: 'relative', height: `${CLIP_ROW_H}px`, width: '100%' });
        for (const p of lane) { const bar = makeClipBar(p); clipRowEls.set(p.id, bar); line.appendChild(bar); }
        row.appendChild(line);
      }
      clipGroupRows.push(row);
      clipStack.appendChild(row);
    }
    clipEmpty.style.display = (clips.length || musicRowEls.size > 0) ? 'none' : 'block';
    // T6 — when merged, the BEDS render as a collapsible group at the bottom of
    // this surface (same bed bars, presentation only). bedRowEls is owned here in
    // the merged path; the separate-band path (renderAmbience) owns it otherwise.
    if (bedsOnSurface) {
      bedGroupHeader.remove();
      for (const el of bedRowEls.values()) el.remove();
      bedRowEls.clear();
      const era = displayEra();
      const beds = opts.placements.filter((p) => p.kind === 'bed' && (!p.scope || p.scope === 'both' || p.scope === era));
      bedGroupCaret.textContent = `${bedsCollapsed ? '▸' : '▾'} BEDS (${beds.length})`;
      clipStack.appendChild(bedGroupHeader);
      if (!bedsCollapsed) for (const p of beds) { const r = makeBedRow(p); bedRowEls.set(p.id, r); clipStack.appendChild(r); }
    }
    refreshMusicBars(); // seed the live labels for the freshly-built bars
  }

  function renderClips(): void {
    repaintClipBars();
    // Selection drives the floating editor (N3) — reconciled, not rebuilt, so an
    // open editor for the same clip keeps focus through the repaint (T1).
    const sel = selectedClipId ? opts.placements.find((x) => x.id === selectedClipId && clipRowEls.has(x.id)) : null;
    if (sel) showClipEditor(sel); else hideClipEditor();
    // T5 — the music slot uses the same select→editor grammar on this surface.
    const selM = selectedMusicId ? opts.placements.find((x) => x.id === selectedMusicId && musicRowEls.has(x.id)) : null;
    if (selM) showMusicEditor(selM); else hideMusicEditor();
  }
  renderClips();
  syncClipArmed(); // T2 — seed the armed-source chip from the default selection
  // T3 — asset-length truth: when a clip's source sample finishes decoding,
  // correct its placeholder dur to the real (clamped) length and drop "len?".
  // Same clamp as creation, so a clip laid pre-decode lands on the same dur it
  // would have had. Only touches clips still pending (a manual edit cleared them).
  const unsubLoaded = devBus.on('audio:loaded', ({ id, duration }) => {
    if (pendingLen.size === 0) return;
    let changed = false;
    for (const p of opts.placements) {
      if (p.kind === 'clip' && p.asset === id && pendingLen.has(p.id)) {
        p.dur = Math.max(2, Math.min(30, duration));
        pendingLen.delete(p.id);
        changed = true;
      }
    }
    if (!changed) return;
    repaintClipBars();
    const sel = selectedClipId ? opts.placements.find((x) => x.id === selectedClipId) : null;
    if (sel && sel.kind === 'clip') refreshClipEditorMeta(sel);
  });
  // U5 grammar: a selection's first empty-lane click deselects; next creates.
  clipStack.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.target !== clipStack) return;
    if (selectedClipId || selectedMusicId) {
      selectedClipId = null;
      selectedMusicId = null; // T5 — clear a selected music slot too
      devBus.emit('hero:select', { id: null });
      renderClips();
      return;
    }
    addClipAt(clipT(e.clientX));
  });

  let selectedCueId: string | null = null;
  const markerEls = new Map<string, HTMLElement>();
  // Hoisted as a function declaration (not a const arrow) so the renderClips()
  // / renderCues() calls earlier in createTimeline's body can format a clip or
  // cue that is ALREADY in soundscape.json at boot. As a const arrow, tHM sat
  // in the temporal dead zone at those early calls, so a clip present at load
  // threw "Cannot access 'tHM' before initialization" and crashed startup —
  // latent since N1/N2 (no clip had ever been persisted to the file before).
  function tHM(t: number): string {
    const mins = ((Math.round(t * 24 * 60) % 1440) + 1440) % 1440;
    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  }
  const trackT = (clientX: number): number => {
    const rect = cueTrack.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
  };
  const makeMarker = (cue: AudioCue, inView: boolean): HTMLElement => {
    const color = channelCssColor(cueChannel(cue) ?? 'sfx');
    const selected = selectedCueId === cue.id;
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { position: 'absolute', left: `${cue.t * 100}%`, top: '50%', transform: 'translate(-50%, -50%)', cursor: 'grab', touchAction: 'none' });
    const dia = document.createElement('div');
    const sz = selected ? 12 : 9;
    // In-view cues fire in the era on screen → solid fill. Off-view cues (gated
    // to the OTHER era) render hollow + dimmed instead of vanishing, so a freshly
    // dropped off-era cue is always visible (no "did my drop work?" confusion).
    Object.assign(dia.style, {
      width: `${sz}px`, height: `${sz}px`, transform: 'rotate(45deg)',
      background: inView ? color : 'transparent',
      border: selected ? '1px solid #fff' : `1px solid ${inView ? 'rgba(0,0,0,0.35)' : color}`,
      opacity: inView ? '1' : '0.5',
      boxShadow: cue.state && cue.state !== 'both'
        ? `0 0 0 2px ${ERA_RING[cue.state] ?? 'transparent'}`
        : (selected ? `0 0 6px ${color}` : 'none'),
    });
    wrap.appendChild(dia);
    wrap.title = `${cue.label ?? cueAssetId(cue) ?? 'cue'} · ${tHM(cue.t)} · ${cue.state ?? 'both'}${inView ? '' : ' · not in the current era (toggle morph to view)'}`;
    if (selected) {
      const pop = document.createElement('div');
      Object.assign(pop.style, { position: 'absolute', bottom: '13px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '2px', background: 'rgba(12,15,20,0.96)', border: `1px solid ${color}`, borderRadius: '4px', padding: '3px 6px', whiteSpace: 'nowrap', zIndex: '6' });
      const topRow = document.createElement('div');
      Object.assign(topRow.style, { display: 'flex', alignItems: 'center', gap: '5px' });
      const lbl = document.createElement('span');
      lbl.textContent = `${cue.label ?? prettyAsset(cueAssetId(cue) ?? 'cue')} ${tHM(cue.t)}`;
      Object.assign(lbl.style, { fontSize: '9px', color: TEXT, flex: '1 1 auto' });
      const del = document.createElement('span');
      del.textContent = '✕';
      Object.assign(del.style, { fontSize: '10px', color: '#ff8a8a', cursor: 'pointer' });
      del.addEventListener('pointerdown', (e) => {
        e.stopPropagation(); e.preventDefault();
        const i = opts.cues.findIndex((c) => c.id === cue.id);
        if (i >= 0) { opts.cues.splice(i, 1); markDirty(); }
        selectedCueId = null;
        renderCues();
      });
      topRow.append(lbl, del);
      // N3 — the full editor: every aspect of a scheduled cue, editable here.
      // Targeting per X3's caveats: era is top-level cue.state; asset/anchor/
      // volume/channel live on the FIRST audio.play action (the cues.ts
      // convention); hand-authored cues with zero audio.play actions get the
      // era/time controls only.
      const play = cue.actions.find((a) => a.kind === 'audio.play') as Extract<AudioCue['actions'][number], { kind: 'audio.play' }> | undefined;
      const fields = document.createElement('div');
      Object.assign(fields.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
      fields.append(
        edRow('t', edNum(cue.t, 0.01, 'time of day (0..1, wraps)', (v) => {
          cue.t = ((v % 1) + 1) % 1; markDirty(); renderCues();
        })),
        edRow('era', edSel([{ v: 'both', label: 'both' }, { v: 'past', label: 'past' }, { v: 'present', label: 'present' }],
          cue.state ?? 'both', 'era gate', (v) => {
            cue.state = v === 'both' ? undefined : (v as StateTag);
            markDirty(); renderCues(); // era ring/dimming bake at marker creation
          })),
      );
      if (play) {
        fields.append(
          edRow('snd', edSel(assetEntriesFor(play.channel ?? 'sfx', play.id), play.id, 'swap the sound (⌁ = synth patch — a data-only swap)', (v) => {
            play.id = v; markDirty(); renderCues();
          })),
          edRow('ch', edSel(['ambient', 'music', 'narration', 'sfx', 'room'].map((c) => ({ v: c, label: c })), play.channel ?? 'sfx', 'channel', (v) => {
            play.channel = v as AudioChannel; markDirty(); renderCues();
          })),
          edRow('⌖', edSel([{ v: '', label: '⌖ none' }, ...opts.heroIds.map((h) => ({ v: h, label: h.replace(/^hero_/, '⌖ ') }))],
            play.at?.heroId ?? '', 're-anchor (spatialize at a hero)', (v) => {
              play.at = v ? { heroId: v } : undefined; // object or undefined — never a '(none)' string
              markDirty(); renderCues();
            })),
          edRow('vol', edNum(play.volume ?? 0.85, 0.05, 'authored volume 0..1 (the action, not the live handle)', (v) => {
            play.volume = Math.max(0, Math.min(1, v)); markDirty();
          })),
        );
      }
      // Audition-in-context (X2): the REAL action path, deterministic — raw
      // actions, no vary roll (no chance gate / jitter / oneOf swap).
      if (opts.runActions) {
        const aud = mkBtn('▸ audition');
        aud.style.fontSize = '9px';
        aud.title = 'play this cue exactly as scheduled (deterministic — vary bypassed)';
        aud.addEventListener('pointerdown', (e) => { e.stopPropagation(); });
        aud.addEventListener('click', (e) => { e.stopPropagation(); opts.runActions!(cue.actions); });
        fields.appendChild(aud);
      }
      pop.append(topRow, fields, makeVaryEditor(cue, true, () => assetSel.value || null, cueAssetId(cue) ?? assetSel.value, markDirty));
      // N9: a synth-backed cue grows the patch instrument.
      if (play) {
        const patchSec = makePatchSection(play.id, (newId) => { play.id = newId; markDirty(); renderCues(); });
        if (patchSec) pop.appendChild(patchSec);
      }
      wrap.appendChild(pop);
    }
    let downX = 0, moved = false, dragging = false;
    wrap.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); e.preventDefault();
      dragging = true; moved = false; downX = e.clientX;
      beginDrag();
      try { wrap.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      wrap.style.cursor = 'grabbing';
    });
    wrap.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (!moved && Math.abs(e.clientX - downX) > 3) { moved = true; flagDirtyDuringDrag(); }
      if (moved) { cue.t = trackT(e.clientX); wrap.style.left = `${cue.t * 100}%`; }
    });
    const up = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false; endDrag(); wrap.style.cursor = 'grab';
      try { wrap.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      if (moved) { markDirty(); renderCues(); }
      else {
        const willSelect = selectedCueId !== cue.id;
        selectedCueId = willSelect ? cue.id : null;
        // A cue anchored to a hero selects it on pick (3D + inspector follow).
        const h = cueHeroId(cue);
        if (h) devBus.emit('hero:select', { id: willSelect ? h : null });
        renderCues();
      }
    };
    wrap.addEventListener('pointerup', up);
    wrap.addEventListener('pointercancel', up);
    // Cross-surface focus: hovering the marker lights this cue's anchored hero
    // + its 3D source + any chip/bed sharing the sound; the diamond glows in
    // its channel color when something else focuses the same sound.
    const fChan = cueChannel(cue) ?? 'sfx';
    const fHero = cueHeroId(cue);
    const fAsset = cueAssetId(cue);
    const fcand: FocusRef = { placementId: cue.id, channel: fChan, ...(fAsset ? { assetId: fAsset } : {}), ...(fHero ? { heroId: fHero } : {}) };
    (wrap as HTMLElement & { _cueId?: string; _cueDia?: HTMLElement })._cueId = cue.id;
    (wrap as HTMLElement & { _cueDia?: HTMLElement })._cueDia = dia;
    bindFocus(wrap, fcand, fChan, dia);
    wrap.addEventListener('mouseenter', () => emitFocus(fcand));
    wrap.addEventListener('mouseleave', () => emitFocus(null));
    return wrap;
  };
  function renderCues(): void {
    for (const el of markerEls.values()) el.remove();
    markerEls.clear();
    // Every cue is ALWAYS rendered. The one(s) gated to the era you're NOT
    // viewing show dimmed + hollow (see makeMarker), full-color otherwise —
    // so dropping a cue can never look like it failed.
    const era = displayEra();
    let inView = 0;
    for (const cue of opts.cues) {
      const cueInView = !cue.state || cue.state === 'both' || cue.state === era;
      if (cueInView) inView++;
      const m = makeMarker(cue, cueInView);
      markerEls.set(cue.id, m);
      cueTrack.appendChild(m);
    }
    cueEmpty.style.display = opts.cues.length === 0 ? 'block' : 'none';
    cuesLbl.title = opts.cues.length
      ? `CUES — ${opts.cues.length} cue${opts.cues.length === 1 ? '' : 's'} · ${inView} firing in the era on screen`
      : 'CUES — time-of-day one-shots; pick a sound + click the track to add one';
  }
  renderCues();
  // Click empty track → drop a cue with the current picker settings.
  // U5 grammar (starting default): while a cue is selected, the first empty-
  // lane click DESELECTS (dismissing the popover) instead of spawning a new
  // cue under it; the next click creates. Esc also deselects.
  cueTrack.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    if (selectedCueId) {
      selectedCueId = null;
      devBus.emit('hero:select', { id: null });
      renderCues();
      return;
    }
    const assetId = assetSel.value;
    if (!assetId) return;
    const channel: AudioChannel = opts.audioAssets.find((a) => a.id === assetId)?.defaultChannel ?? 'sfx';
    const anchor = anchorSel.value;
    const era = eraSel.value;
    // N10 — the cue/clip merge: this strip now drops a TRIGGER clip (the unified
    // citizen), which appears as a ▶ bar in the CLIPS lane below. cues[] is
    // retired, so a drop goes straight into placements.
    const trig: SoundPlacement = {
      id: 'clip_' + Math.random().toString(36).slice(2, 9),
      kind: 'clip', playback: 'trigger',
      asset: assetId, scope: (era === 'both' ? 'both' : era) as StateTag, channel,
      t: trackT(e.clientX),
      dur: Math.max(2, Math.min(30, opts.audio.assetDuration(assetId) ?? 8)),
      gain: 0.85,
      ...(anchor !== '(none)' ? { at: { heroId: anchor } } : {}),
    };
    opts.placements.push(trig);
    selectedClipId = trig.id;
    markDirty();
    renderClips();
  });
  // Arm → drop preview. While a one-shot is armed (here or in the palette), a
  // faint diamond tracks the cursor over the CUES track in the armed sound's
  // channel color — so it's clear WHERE a click lands and on WHICH channel,
  // closing the loop between the audio palette and the timeline. (Loops are
  // previewed by the BEDS brush label instead.)
  const cueGhost = document.createElement('div');
  Object.assign(cueGhost.style, { position: 'absolute', top: '50%', width: '9px', height: '9px', transform: 'translate(-50%, -50%) rotate(45deg)', pointerEvents: 'none', opacity: '0', transition: 'opacity 0.12s', zIndex: '4' });
  cueTrack.appendChild(cueGhost);
  cueTrack.addEventListener('pointermove', (e) => {
    const id = assetSel.value;
    if (!id) { cueGhost.style.opacity = '0'; return; }
    const c = channelCssColor(opts.audioAssets.find((a) => a.id === id)?.defaultChannel ?? 'sfx');
    cueGhost.style.background = c + '55';
    cueGhost.style.border = `1px solid ${c}`;
    cueGhost.style.left = `${trackT(e.clientX) * 100}%`;
    cueGhost.style.opacity = '0.75';
    cueEmpty.style.opacity = '0'; // the ghost previews the drop while hovering
  });
  cueTrack.addEventListener('pointerleave', () => {
    cueGhost.style.opacity = '0';
    if (opts.cues.length === 0) cueEmpty.style.opacity = '0.7';
  });
  // The AUDIO palette arms a sound → make it this lane's drop brush + flash
  // so the connection is visible. Programmatic .value set fires no 'change',
  // so this can't loop back through the listener above.
  const unsubArm = devBus.on('audio:arm', ({ id }) => {
    if (!id || ![...assetSel.options].some((o) => o.value === id)) return;
    // Loops belong to the AMBIENCE (BEDS) lane — let that brush claim them.
    if (opts.audioAssets.find((a) => a.id === id)?.loopable) return;
    assetSel.value = id;
    syncClipArmed(); // T2 — palette arm reflects on the CLIPS lane too
    assetSel.animate([{ boxShadow: `0 0 0 2px ${ACCENT}` }, { boxShadow: '0 0 0 0 rgba(0,0,0,0)' }], { duration: 700, easing: 'ease-out' });
    // CUES is the hot drop-lane now — tint its label in the armed channel.
    cuesLbl.style.color = channelCssColor(opts.audioAssets.find((a) => a.id === id)?.defaultChannel ?? 'sfx');
    ambLbl.style.color = MUTED;
  });
  // Flash a marker when its cue fires.
  const unsubCueFire = devBus.on('cue:fire', ({ id }) => {
    const m = markerEls.get(id);
    if (!m) return;
    m.animate([
      { transform: 'translate(-50%, -50%) scale(1)', filter: 'brightness(1)' },
      { transform: 'translate(-50%, -50%) scale(1.9)', filter: 'brightness(1.9)' },
      { transform: 'translate(-50%, -50%) scale(1)', filter: 'brightness(1)' },
    ], { duration: 650, easing: 'ease-out' });
  });

  // ── ambience lane (looping world beds, per era) ─────────────────────
  // The continuous counterpart to CUES. Each bar is a BED — a loop that holds
  // a window of the day for one era. Bars sit on the time-of-day axis (same as
  // TIME/CUES) and are bottom-aligned with height = gain, so the lane reads
  // like a level map of the day. Drag a bar body sideways to slide its window,
  // up/down to set its gain; drag an edge grip to trim a side; click to select
  // (✕ deletes). Arm a LOOP in the palette, then click empty track to drop a
  // new bed anchored to the chosen hero. Shows only the era you're viewing;
  // toggling MORPH repopulates the day. Edits are live — the SoundscapeEngine
  // reconciles beds (start/stop + gain) every frame.
  const ambRow = document.createElement('div');
  Object.assign(ambRow.style, { display: 'flex', alignItems: 'flex-start', gap: '8px', position: 'relative' }); // T7 — top-align; the bed stack grows downward
  const ambLbl = document.createElement('div');
  // DM10 — the merge-onto-surface affordance was a title tooltip only. Show the
  // ⤢ glyph in the label itself so it reads as a clickable control (matches the
  // merged-state "⤢ band" flip-back, which was already visible).
  ambLbl.textContent = 'BEDS ⤢';
  ambLbl.title = 'ambient beds — looping world sound per era';
  Object.assign(ambLbl.style, { color: MUTED, fontSize: '9px', letterSpacing: '0.5px', width: '44px', flexShrink: '0' });
  // Each bed gets its own row in a scrollable stack, so beds never overlap and
  // each is individually selectable + deletable. Ticks + playhead span the whole
  // stack (they share the time axis with TIME/CUES above).
  const ambStack = document.createElement('div');
  // T7 — auto-size to the bed count: a thin rail when empty, grows per bed,
  // scrolls past a max (was a fixed 72px band even with one bed).
  Object.assign(ambStack.style, { flex: '1 1 auto', minWidth: '0', position: 'relative', minHeight: '20px', overflow: 'hidden', background: TRACK_BG, borderRadius: '3px', cursor: 'copy' });
  for (const s of TIME_SNAPS) {
    const tick = document.createElement('div');
    Object.assign(tick.style, { position: 'absolute', left: `${tToFrac(s.t) * 100}%`, top: '0', bottom: '0', width: '1px', background: TICK_COLOR, pointerEvents: 'none', zIndex: '1' }); // W3b — window-mapped
    ambStack.appendChild(tick);
  }
  const ambHead = document.createElement('div');
  Object.assign(ambHead.style, { position: 'absolute', left: '0%', top: '0', bottom: '0', width: '1px', background: ACCENT, opacity: '0.5', pointerEvents: 'none', zIndex: '5' });
  ambStack.appendChild(ambHead);
  const ambEmpty = document.createElement('div');
  ambEmpty.textContent = 'arm a loop → + bed';
  Object.assign(ambEmpty.style, { position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: '9px', fontStyle: 'italic', opacity: '0.7', pointerEvents: 'none' });
  ambStack.appendChild(ambEmpty);
  const ambBrush = document.createElement('span');
  Object.assign(ambBrush.style, { fontSize: '9px', width: '92px', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
  // Bed sound picker (John, 2026-06-29: "need options for the bed sound" — it only
  // gave fan oscillation, and the loopable-only list still missed sounds he wanted,
  // e.g. the generator). Now lists EVERY effect sound — all ambient + sfx (i.e.
  // everything except the music + narration channels, which aren't beds); the bed
  // engine loops whatever you pick, so one-shots are usable too (marked ·1shot).
  const bedAssetSel = document.createElement('select');
  Object.assign(bedAssetSel.style, selStyle);
  bedAssetSel.style.width = '118px';
  bedAssetSel.title = 'bed sound — any ambient/effect sound; the bed loops it (·1shot = not a seamless loop)';
  for (const a of opts.audioAssets.filter((x) => x.defaultChannel !== 'music' && x.defaultChannel !== 'narration')) {
    const o = document.createElement('option'); o.value = a.id; o.textContent = prettyAsset(a.id) + (a.loopable ? '' : ' ·1shot'); bedAssetSel.appendChild(o);
  }
  // The dropdown is the authoritative bed picker → set armedBedAsset directly (the
  // audio:arm handler rejects non-loopable, so don't rely on it); still emit arm so
  // a loopable pick also lights the BEDS lane in the palette.
  bedAssetSel.addEventListener('change', () => { armedBedAsset = bedAssetSel.value; updateAmbBrush(); devBus.emit('audio:arm', { id: bedAssetSel.value }); });
  const ambAnchorSel = document.createElement('select');
  Object.assign(ambAnchorSel.style, selStyle);
  for (const h of ['(none)', ...opts.heroIds]) {
    const o = document.createElement('option'); o.value = h; o.textContent = h === '(none)' ? '⌖ none' : h.replace(/^hero_/, '⌖ '); ambAnchorSel.appendChild(o);
  }
  ambAnchorSel.title = 'hero this bed is spatialized to';
  ambAnchorSel.style.width = '80px';
  const addBedBtn = mkBtn('+ bed');
  addBedBtn.style.fontSize = '10px';
  addBedBtn.title = 'add an all-day bed for the armed loop';
  addBedBtn.addEventListener('click', () => addBed());
  const saveBedsBtn = mkSaveBtn();
  ambRow.append(ambLbl, ambStack, makeBrushReveal('beds brush', [ambBrush, bedAssetSel, ambAnchorSel, addBedBtn, saveBedsBtn], 'pick the bed loop (or arm one in the AUDIO palette) + an optional anchor hero, then + bed (or click the lane) to drop. ⌘S (or save) writes to disk.'));
  lanes.appendChild(ambRow);
  // T6 — the active bed container: clipStack when merged onto the surface, else
  // the separate ambStack. Bed bars, their drag/editor geometry, and the empty-
  // click create all read this. The on-surface BEDS group is a collapsible header
  // (caret toggles, "+ bed" adds, "⤢ band" flips back) built once + mounted by
  // repaintClipBars when merged. The off-surface label flips the other way.
  const bedStack = (): HTMLDivElement => (bedsOnSurface ? clipStack : ambStack);
  ambLbl.style.cursor = 'pointer';
  ambLbl.title = 'BEDS — click to merge onto the unified clip surface (⤢); reversible';
  ambLbl.addEventListener('click', () => setBedsOnSurface(true));
  const bedGroupHeader = document.createElement('div');
  Object.assign(bedGroupHeader.style, { position: 'relative', width: '100%', flexShrink: '0', display: 'flex', alignItems: 'center', gap: '6px', height: '15px', zIndex: '2' });
  const bedGroupCaret = document.createElement('span');
  Object.assign(bedGroupCaret.style, { fontSize: '9px', color: MUTED, cursor: 'pointer', userSelect: 'none' });
  bedGroupCaret.addEventListener('click', (e) => { e.stopPropagation(); bedsCollapsed = !bedsCollapsed; renderClips(); });
  const bedGroupAdd = document.createElement('span');
  bedGroupAdd.textContent = '+ bed';
  Object.assign(bedGroupAdd.style, { fontSize: '8px', color: channelCssColor('ambient'), cursor: 'pointer', border: `1px solid ${channelCssColor('ambient')}66`, borderRadius: '3px', padding: '0 4px' });
  bedGroupAdd.addEventListener('click', (e) => { e.stopPropagation(); addBed(); });
  const bedGroupFlip = document.createElement('span');
  bedGroupFlip.textContent = '⤢ band';
  Object.assign(bedGroupFlip.style, { fontSize: '8px', color: MUTED, cursor: 'pointer', marginLeft: 'auto' });
  bedGroupFlip.title = 'flip beds back to their own band (reversible)';
  bedGroupFlip.addEventListener('click', (e) => { e.stopPropagation(); setBedsOnSurface(false); });
  bedGroupHeader.append(bedGroupCaret, bedGroupAdd, bedGroupFlip);

  // The armed loop is the BEDS drop brush — synced from the palette (audio:arm)
  // but only for loopable assets; one-shots stay with the CUES brush.
  let armedBedAsset: string | null = opts.audioAssets.find((a) => a.loopable)?.id ?? null;
  const updateAmbBrush = (): void => {
    ambBrush.textContent = armedBedAsset ? `▶ ${prettyAsset(armedBedAsset)}` : '(arm a loop)';
    ambBrush.style.color = armedBedAsset ? ACCENT : MUTED;
    // Keep the picker in sync when a loop is armed from the AUDIO palette instead.
    if (armedBedAsset && bedAssetSel.value !== armedBedAsset) bedAssetSel.value = armedBedAsset;
  };
  updateAmbBrush();

  let selectedBedId: string | null = null;
  const bedRowEls = new Map<string, HTMLElement>();
  const ROW_H = 16;
  const ambT = (clientX: number): number => {
    const rect = ambStack.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
  };
  // One row per bed — beds never overlap. The bar shows its day-window on the
  // time axis with gain as a bottom fill; selecting it opens an editor OVERLAY
  // above the strip (showBedEditor), so the stack never grows or scrolls.
  const makeBedRow = (p: SoundPlacement): HTMLElement => {
    const color = channelCssColor(p.channel ?? 'ambient');
    const selected = selectedBedId === p.id;
    const [s0, s1] = p.span ?? [0, 1];
    const gain = p.gain ?? 0.4;
    const heroLabel = (): string => (p.at?.heroId ? p.at.heroId.replace(/^hero_/, '') : prettyAsset(p.asset ?? '(drops)'));
    const row = document.createElement('div');
    Object.assign(row.style, { position: 'relative', width: '100%', flexShrink: '0', zIndex: '2', pointerEvents: 'none' });
    // ── bar line (the time-axis row) ──
    const line = document.createElement('div');
    Object.assign(line.style, { position: 'relative', height: `${ROW_H}px`, width: '100%' });
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      pointerEvents: 'auto', // parent row/line are pe:none (empty clicks reach the stack → add bed); the bar stays interactive
      position: 'absolute', left: `${s0 * 100}%`, width: `${Math.max(0, s1 - s0) * 100}%`,
      top: '1px', bottom: '1px', minWidth: '4px',
      background: color + (selected ? '4d' : '22'), border: `1px solid ${color}${selected ? '' : '99'}`,
      borderRadius: '2px', boxSizing: 'border-box', cursor: 'grab', touchAction: 'none', overflow: 'hidden',
    });
    const fill = document.createElement('div');
    Object.assign(fill.style, { position: 'absolute', left: '0', right: '0', bottom: '0', height: `${Math.max(0.06, gain) * 100}%`, background: color + '4d', pointerEvents: 'none' });
    const lbl = document.createElement('span');
    lbl.textContent = `${heroLabel()} · ${Math.round(gain * 100)}%`;
    Object.assign(lbl.style, { position: 'absolute', left: '4px', top: '0', fontSize: '8px', lineHeight: `${ROW_H}px`, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', pointerEvents: 'none', opacity: '0.92' });
    const mkGrip = (side: 'left' | 'right'): HTMLDivElement => {
      const g = document.createElement('div');
      Object.assign(g.style, { position: 'absolute', top: '0', bottom: '0', [side]: '0', width: '5px', cursor: 'ew-resize' });
      return g;
    };
    const gripL = mkGrip('left'); const gripR = mkGrip('right');
    // hover ✕ — one-click delete, no need to open the editor first
    const del = document.createElement('span');
    del.textContent = '✕';
    // right:7px keeps the ✕ clear of the 5px right resize grip (U1) — they used
    // to overlap (✕ at right:1px, zIndex 4, on top), so reaching for resize
    // one-click-deleted the bed. Now the rightmost 5px is grip-only.
    Object.assign(del.style, { position: 'absolute', right: '7px', top: '0', fontSize: '9px', lineHeight: `${ROW_H}px`, color: '#ff9a9a', cursor: 'pointer', opacity: '0', zIndex: '4', padding: '0 2px' });
    del.addEventListener('pointerdown', (e) => { e.stopPropagation(); e.preventDefault(); removeBed(p.id); });
    // N5 — the "why aren't you sounding?" badge, painted per frame in refresh():
    // ▶ playing · ⊘ chance-skipped · ⚠ asset missing · (blank) silent/off.
    const statusEl = document.createElement('span');
    Object.assign(statusEl.style, { position: 'absolute', right: '20px', top: '0', fontSize: '8px', lineHeight: `${ROW_H}px`, pointerEvents: 'none', zIndex: '3' });
    bar.appendChild(statusEl);
    (row as HTMLElement & { _bedId?: string; _bedStatusEl?: HTMLElement; _bedAsset?: string })._bedId = p.id;
    (row as HTMLElement & { _bedStatusEl?: HTMLElement })._bedStatusEl = statusEl;
    (row as HTMLElement & { _bedAsset?: string })._bedAsset = p.asset;
    // Cross-surface focus: hovering the bar reveals its ✕ AND lights this bed's
    // anchored hero + 3D source + any chip/cue sharing the sound.
    const bcand: FocusRef = { placementId: p.id, assetId: p.asset, channel: p.channel ?? 'ambient', ...(p.at?.heroId ? { heroId: p.at.heroId } : {}) };
    bar.addEventListener('mouseenter', () => { del.style.opacity = '1'; emitFocus(bcand); });
    bar.addEventListener('mouseleave', () => { del.style.opacity = '0'; emitFocus(null); });
    bar.append(fill, lbl, gripL, gripR, del);
    line.appendChild(bar);
    row.appendChild(line);
    // The editor is NOT inline — selecting a bed opens it as an overlay popover
    // above the strip (showBedEditor), so it never grows this stack or scrolls.
    // ── bar drag: axis-locked move(x)/gain(y); a click selects/deselects ──
    let dragging = false, moved = false, mode: 'move' | 'gain' | null = null;
    let downX = 0, downY = 0, startS0 = 0, startS1 = 0, startGain = 0;
    bar.addEventListener('pointerdown', (e) => {
      if (e.target === gripL || e.target === gripR || e.target === del) return;
      e.stopPropagation(); e.preventDefault();
      dragging = true; moved = false; mode = null;
      beginDrag();
      downX = e.clientX; downY = e.clientY;
      const sp = p.span ?? [0, 1]; startS0 = sp[0]; startS1 = sp[1]; startGain = p.gain ?? 0.4;
      try { bar.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      bar.style.cursor = 'grabbing';
    });
    bar.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - downX, dy = e.clientY - downY;
      if (!mode) {
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        mode = Math.abs(dx) >= Math.abs(dy) ? 'move' : 'gain';
        moved = true;
        flagDirtyDuringDrag();
      }
      const rect = bedStack().getBoundingClientRect();
      if (mode === 'move') {
        const w = startS1 - startS0;
        const ns0 = Math.max(0, Math.min(1 - w, startS0 + dx / Math.max(1, rect.width)));
        p.span = [ns0, ns0 + w];
        bar.style.left = `${ns0 * 100}%`;
      } else {
        const ng = Math.max(0, Math.min(1, startGain - dy / 80));
        p.gain = ng;
        fill.style.height = `${Math.max(0.06, ng) * 100}%`;
        lbl.textContent = `${heroLabel()} · ${Math.round(ng * 100)}%`;
      }
    });
    const up = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false; endDrag(); bar.style.cursor = 'grab';
      try { bar.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      if (moved) { markDirty(); renderAmbience(); }
      else {
        const willSelect = selectedBedId !== p.id;
        selectedBedId = willSelect ? p.id : null;
        selectedClipId = null; selectedMusicId = null; // T8 — one selection at a time across citizens
        // Selecting a bed makes its anchored hero the active selection (3D
        // outline + inspector hero row follow); deselecting clears it.
        if (p.at?.heroId) devBus.emit('hero:select', { id: willSelect ? p.at.heroId : null });
        renderAmbience();
        renderClips(); // hide a now-stale clip/music editor
      }
    };
    bar.addEventListener('pointerup', up);
    bar.addEventListener('pointercancel', up);
    const wireGrip = (grip: HTMLElement, which: 'L' | 'R'): void => {
      let active = false, moved = false;
      grip.addEventListener('pointerdown', (e) => {
        e.stopPropagation(); e.preventDefault(); active = true; moved = false; beginDrag();
        try { grip.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      });
      grip.addEventListener('pointermove', (e) => {
        if (!active) return;
        if (!moved) { moved = true; flagDirtyDuringDrag(); } // U3: dirty on first effective resize
        const t = ambT(e.clientX);
        const sp = p.span ?? [0, 1];
        const MIN = 0.02;
        p.span = which === 'L' ? [Math.min(t, sp[1] - MIN), sp[1]] : [sp[0], Math.max(t, sp[0] + MIN)];
        bar.style.left = `${p.span[0] * 100}%`;
        bar.style.width = `${Math.max(0, p.span[1] - p.span[0]) * 100}%`;
      });
      const gup = (e: PointerEvent): void => {
        if (!active) return; active = false; endDrag();
        try { grip.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if (moved) { markDirty(); renderAmbience(); } // skip a no-move grip click (no undo churn)
      };
      grip.addEventListener('pointerup', gup);
      grip.addEventListener('pointercancel', gup);
    };
    wireGrip(gripL, 'L'); wireGrip(gripR, 'R');
    bindFocus(row, bcand, p.channel ?? 'ambient', bar);
    return row;
  };

  // ── bed editor (OVERLAY popover, not inline) ────────────────────────
  // Selecting a bed opens its editor as a popover floating ABOVE the strip,
  // anchored over the bar — so it never expands the BEDS stack or forces a
  // scroll. One shared popover, repopulated for the current selection.
  const bedEditorPop = document.createElement('div');
  Object.assign(bedEditorPop.style, {
    position: 'absolute', bottom: 'calc(100% + 8px)', zIndex: '40', display: 'none',
    flexDirection: 'column', gap: '2px', padding: '6px 8px',
    background: 'rgba(12,15,20,0.97)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '6px', boxShadow: '0 6px 24px rgba(0,0,0,0.45)', minWidth: '210px',
  });
  bedEditorPop.addEventListener('pointerdown', (e) => e.stopPropagation());
  ambRow.appendChild(bedEditorPop);
  // T6 — flip beds between the separate band and the unified surface (reversible).
  // Re-parents the floating bed editor over the active surface + re-renders both.
  const setBedsOnSurface = (on: boolean): void => {
    if (on === bedsOnSurface) return;
    bedsOnSurface = on;
    ambRow.style.display = on ? 'none' : 'flex';
    (on ? clipRow : ambRow).appendChild(bedEditorPop);
    bedGroupHeader.remove(); // drop the on-surface group header; a merged re-render re-adds it
    renderAmbience();
    renderClips();
  };
  const hideBedEditor = (): void => { bedEditorPop.style.display = 'none'; };
  const showBedEditor = (p: SoundPlacement): void => {
    bedEditorPop.replaceChildren();
    const [s0, s1] = p.span ?? [0, 1];
    const head = document.createElement('div');
    Object.assign(head.style, { display: 'flex', alignItems: 'center', gap: '5px' });
    const info = document.createElement('span');
    info.textContent = `${prettyAsset(p.asset ?? '(drops)')} · ${tHM(s0)}–${s1 >= 1 ? '24:00' : tHM(s1)}`;
    Object.assign(info.style, { fontSize: '8.5px', color: MUTED, flex: '1 1 auto', whiteSpace: 'nowrap', overflow: 'hidden' });
    const scopeSel = document.createElement('select');
    Object.assign(scopeSel.style, selStyle); scopeSel.style.fontSize = '8.5px';
    for (const s of ['past', 'present', 'both']) { const o = document.createElement('option'); o.value = s; o.textContent = s; scopeSel.appendChild(o); }
    scopeSel.value = p.scope; scopeSel.title = 'era scope';
    scopeSel.addEventListener('change', () => { p.scope = scopeSel.value as StateTag; markDirty(); renderAmbience(); });
    const chanSel = document.createElement('select');
    Object.assign(chanSel.style, selStyle); chanSel.style.fontSize = '8.5px';
    for (const c of ['ambient', 'room', 'sfx']) { const o = document.createElement('option'); o.value = c; o.textContent = c; chanSel.appendChild(o); }
    chanSel.value = p.channel ?? 'ambient'; chanSel.title = 'output channel';
    chanSel.addEventListener('change', () => { p.channel = chanSel.value as AudioChannel; markDirty(); });
    head.append(info, scopeSel, chanSel);
    bedEditorPop.append(head, makeSpatialEditor(p, markDirty), makeVaryEditor(p, false, () => armedBedAsset, p.asset ?? '', markDirty), makeModEditor(p, markDirty));
    if (opts.auditionClip) {
      // T8 — beds gain the same audition affordance as clips + music.
      const aud = mkBtn('▸ audition');
      aud.style.fontSize = '8.5px';
      aud.title = 'play this bed through the real chain at its anchor (click again to stop)';
      aud.addEventListener('pointerdown', (e) => e.stopPropagation());
      aud.addEventListener('click', (e) => { e.stopPropagation(); opts.auditionClip!(p); });
      bedEditorPop.appendChild(aud);
    }
    // Anchor over the bar's left edge, clamped within the lane row.
    bedEditorPop.style.display = 'flex';
    const stackRect = bedStack().getBoundingClientRect();
    const rowRect = (bedsOnSurface ? clipRow : ambRow).getBoundingClientRect();
    const barLeft = (stackRect.left - rowRect.left) + (p.span?.[0] ?? 0) * stackRect.width;
    bedEditorPop.style.left = `${Math.max(0, Math.min(barLeft, rowRect.width - bedEditorPop.offsetWidth))}px`;
  };

  const addBed = (): void => {
    if (!armedBedAsset) return;
    const asset = opts.audioAssets.find((a) => a.id === armedBedAsset);
    if (!asset) return;
    const anchor = ambAnchorSel.value;
    const ch: AudioChannel = asset.defaultChannel && asset.defaultChannel !== 'music' ? asset.defaultChannel : 'ambient';
    const bed: SoundPlacement = {
      id: newBedId(), kind: 'bed', asset: armedBedAsset, scope: displayEra(), channel: ch,
      at: anchor !== '(none)' ? { heroId: anchor } : undefined,
      spatial: anchor !== '(none)' ? spatialForHero(anchor) : 'global',
      gain: 0.4, span: [0, 1], loop: true,
    };
    opts.placements.push(bed);
    selectedBedId = bed.id;
    markDirty();
    renderAmbience();
  };
  const removeBed = (id: string): void => {
    const i = opts.placements.findIndex((x) => x.id === id);
    if (i >= 0) { opts.placements.splice(i, 1); markDirty(); }
    if (selectedBedId === id) selectedBedId = null;
    renderAmbience();
  };
  function renderAmbience(): void {
    if (bedsOnSurface) {
      repaintClipBars(); // T6 — beds render inline on the unified surface
    } else {
      for (const el of bedRowEls.values()) el.remove();
      bedRowEls.clear();
      const era = displayEra();
      let any = false;
      for (const p of opts.placements) {
        if (p.kind !== 'bed') continue;
        if (p.scope && p.scope !== 'both' && p.scope !== era) continue;
        const r = makeBedRow(p);
        bedRowEls.set(p.id, r);
        ambStack.appendChild(r);
        any = true;
      }
      ambEmpty.style.display = any ? 'none' : 'block';
    }
    // Show the selected bed's editor as an overlay — only while that bed is
    // actually visible in the current era (else hide it, no orphan popover).
    const sel = selectedBedId ? opts.placements.find((x) => x.id === selectedBedId && bedRowEls.has(x.id)) : null;
    if (sel) showBedEditor(sel); else hideBedEditor();
  }
  renderAmbience();
  // Click empty stack area → add a bed (same as the + bed button).
  // U5 grammar: while a bed is selected, the first empty-lane click DESELECTS
  // (closing the editor popover) instead of dropping — and audibly starting —
  // a new bed under it; the next click creates. Esc also deselects.
  ambStack.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.target !== ambStack) return;
    if (selectedBedId) {
      selectedBedId = null;
      devBus.emit('hero:select', { id: null });
      renderAmbience(); // hides the editor popover
      return;
    }
    addBed();
  });
  // Palette arm → if it's a loop, claim it as the BEDS brush + flash.
  const unsubArmBed = devBus.on('audio:arm', ({ id }) => {
    if (!id || !opts.audioAssets.find((a) => a.id === id)?.loopable) return;
    armedBedAsset = id;
    updateAmbBrush();
    ambBrush.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 600, easing: 'ease-out' });
    // BEDS is the hot drop-lane now — tint its label in the armed channel.
    ambLbl.style.color = channelCssColor(opts.audioAssets.find((a) => a.id === id)?.defaultChannel ?? 'ambient');
    cuesLbl.style.color = MUTED;
  });

  // ── sound lane (live "what's playing now") ──────────────────────────
  // Not a scrub track — a live readout of every sounding source, so the
  // strip shows what the TIME + MORPH drivers (and any interaction) are
  // actually producing. This is the seam where "events of time" and "events
  // of interaction" meet: both just surface here as sound. Each chip is
  // colored by channel (same language as the 3D source spheres), marked ∞ if
  // it loops (a world bed) vs a bare one-shot event, and labeled by the hero
  // it's anchored to. Hover a chip to highlight that hero; click to select.
  // Event-driven (audio:play/stop) with a diff so chips keep their pulse.
  // The live SOUND chips no longer get a full-width lane — they're mounted in
  // the right-edge readouts column under the clock (see ── right-edge readouts
  // below), a vertical right-aligned stack. "silent" shows when nothing sounds.
  const chipWrap = document.createElement('div');
  Object.assign(chipWrap.style, { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', overflow: 'hidden', minHeight: '0' });
  const silentHint = document.createElement('div');
  silentHint.textContent = 'silent';
  Object.assign(silentHint.style, { color: MUTED, fontSize: '10px', fontStyle: 'italic', opacity: '0.6' });
  chipWrap.appendChild(silentHint);

  const chipEls = new Map<string, HTMLElement>();
  const makeChip = (handle: string, id: string, channel: AudioChannel, loop: boolean): HTMLElement => {
    const obj = opts.audio.getHandleObject(handle);
    const heroId = obj ? getHeroId(obj) : undefined;
    const color = channelCssColor(channel);
    const chip = document.createElement('div');
    Object.assign(chip.style, {
      display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: '0',
      padding: '2px 8px', borderRadius: '10px',
      background: color + '26', border: `1px solid ${color}73`,
      color: TEXT, fontSize: '10px', whiteSpace: 'nowrap',
    });
    const dot = document.createElement('span');
    Object.assign(dot.style, { width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: '0' });
    const text = document.createElement('span');
    text.textContent = (loop ? '∞ ' : '') + (heroId ? heroId.replace(/^hero_/, '') : prettyAsset(id));
    chip.appendChild(dot);
    chip.appendChild(text);
    chip.title = `${id} · ${channel}` + (heroId ? ` · ${heroId}` : '') + (loop ? ' · loop' : ' · one-shot');
    // Cross-surface focus: every chip (anchored or not) lights its sound
    // everywhere on hover — its 3D source sphere, plus any bed/cue/palette
    // entry sharing the asset; an anchored chip also selects its hero on click.
    const ccand: FocusRef = { handle, assetId: id, channel, ...(heroId ? { heroId } : {}) };
    bindFocus(chip, ccand, channel);
    chip.addEventListener('mouseenter', () => emitFocus(ccand));
    chip.addEventListener('mouseleave', () => emitFocus(null));
    if (heroId) {
      chip.style.cursor = 'pointer';
      chip.addEventListener('click', () => devBus.emit('hero:select', { id: heroId }));
    } else {
      chip.style.cursor = 'default';
    }
    // Entrance pop for every chip; a slow breath for loops so the persistent
    // world beds visibly "live" while one-shots read as steady, brief marks.
    chip.animate([{ transform: 'scale(0.85)', opacity: 0 }, { transform: 'scale(1)', opacity: 1 }], { duration: 180, easing: 'ease-out' });
    if (loop) chip.animate([{ opacity: 0.62 }, { opacity: 1 }, { opacity: 0.62 }], { duration: 2400, iterations: Infinity });
    return chip;
  };
  const syncSound = (): void => {
    const seen = new Set<string>();
    for (const p of opts.audio.listPlaying()) {
      seen.add(p.handle);
      if (!chipEls.has(p.handle)) {
        const chip = makeChip(p.handle, p.id, p.channel, p.loop);
        chipEls.set(p.handle, chip);
        chipWrap.appendChild(chip);
      }
    }
    for (const [k, el] of [...chipEls]) {
      if (!seen.has(k)) { el.remove(); chipEls.delete(k); }
    }
    silentHint.style.display = chipEls.size ? 'none' : 'block';
  };
  syncSound();
  const unsubSoundPlay = devBus.on('audio:play', syncSound);
  const unsubSoundStop = devBus.on('audio:stop', syncSound);

  // Repaint every live lane element's focus glow when the cross-surface focus
  // changes. Freshly (re)rendered elements already paint themselves at create
  // time via bindFocus, so this only handles focus changes against the
  // currently-mounted chips / markers / bed rows.
  const applyFocus = (): void => {
    for (const c of chipEls.values()) paintFocusEl(c as FocusEl);
    for (const m of markerEls.values()) paintFocusEl(m as FocusEl);
    for (const r of bedRowEls.values()) paintFocusEl(r as FocusEl);
    // B2 — clip rows were omitted here, so a clip bar's glow never repainted on
    // a focus change: it stayed lit after the hover moved on, and a selected
    // hero's clips never lit with its beds/cues. Repaint them with the rest.
    for (const cr of clipRowEls.values()) paintFocusEl(cr as FocusEl);
  };
  const unsubFocus = devBus.on('audio:focus', ({ ref }) => { currentFocus = ref; applyFocus(); });
  // Selecting a hero anywhere (3D click, SOUND chip, inspector row, or one of
  // our own beds/cues) pins the focus to it, so its beds + cues stay lit in the
  // lanes while you work on them; scroll the BEDS stack to its first bed so an
  // off-screen one isn't missed.
  const unsubSelectFocus = devBus.on('hero:select', ({ id }) => {
    pinnedFocus = id ? { heroId: id } : null;
    applyFocus();
    if (id) {
      const era = displayEra();
      const target = opts.placements.find((p) => p.kind === 'bed' && p.at?.heroId === id && (!p.scope || p.scope === 'both' || p.scope === era));
      if (target) bedRowEls.get(target.id)?.scrollIntoView({ block: 'nearest' });
    }
  });

  // ── right-edge readouts ────────────────────────────────────────────
  const readouts = document.createElement('div');
  Object.assign(readouts.style, {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: '6px',
    paddingLeft: '6px',
    // The HH:MM clock + the live SOUND chips (relocated here from the old
    // full-width SOUND lane), right-aligned + fixed width so they never resize
    // the lanes. The era/morph label is GONE — the transport's past⇄present
    // toggle is the single source of truth (no duplicate readout).
    width: '104px',
    flexShrink: '0',
    boxSizing: 'border-box',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    marginLeft: '6px',
    textAlign: 'right',
    overflow: 'hidden',
  });
  const timeChip = document.createElement('div');
  timeChip.textContent = opts.clock.formatHM();
  timeChip.style.color = ACCENT;
  timeChip.style.fontSize = '12px';
  timeChip.style.fontWeight = '600';
  timeChip.style.flexShrink = '0';
  readouts.appendChild(timeChip);
  readouts.appendChild(chipWrap); // SOUND chips live under the clock now
  root.appendChild(readouts);

  // ── per-frame refresh (poll, since time:t doesn't fire on tick) ─────
  // Re-render the era-filtered schedule lanes only when the viewed day flips.
  // PF6 — every write here is write-on-change (cached last value): a paused/
  // settled dev view writes 0 DOM properties/frame; only what actually moved
  // (the playhead while running, a changed readout) touches the DOM.
  let lastDisplayEra = displayEra();
  let lastT = -1, lastHM = '', lastPlayLbl = '';
  function refresh(): void {
    const t = opts.clock.t;
    if (t !== lastT) {
      lastT = t;
      const pct = `${tToFrac(t) * 100}%`; // W3b — playheads at the window-mapped fraction
      timeLane.fill.style.width = pct;
      timeLane.head.style.left = pct;
      ambHead.style.left = pct;
      clipHeadEl.style.left = pct;
    }
    refreshMusicBars();
    // N5 — hidden states revealed, all write-on-change:
    const suspended = opts.clock.running && opts.audio.contextState !== 'running';
    const bannerVis = suspended ? 'block' : 'none';
    if (silentBanner.style.display !== bannerVis) silentBanner.style.display = bannerVis;
    if (opts.bedState) {
      for (const [id, rowEl] of bedRowEls) {
        const r = rowEl as HTMLElement & { _bedStatusEl?: HTMLElement; _bedAsset?: string };
        if (!r._bedStatusEl) continue;
        const st = r._bedAsset && !opts.audio.isLoaded(r._bedAsset) ? 'missing' : opts.bedState(id);
        if (r._bedStatusEl.dataset.st === st) continue;
        r._bedStatusEl.dataset.st = st;
        if (st === 'playing') { r._bedStatusEl.textContent = '▶'; r._bedStatusEl.style.color = '#6acf91'; r._bedStatusEl.title = ''; }
        else if (st === 'chance-skipped') { r._bedStatusEl.textContent = '⊘'; r._bedStatusEl.style.color = '#ffd87c'; r._bedStatusEl.title = 'chance roll skipped this activation — silent until the bed re-enters its window'; }
        else if (st === 'missing') { r._bedStatusEl.textContent = '⚠'; r._bedStatusEl.style.color = '#ff8a8a'; r._bedStatusEl.title = 'asset not loaded — check the registry'; }
        else { r._bedStatusEl.textContent = ''; }
      }
    }
    if (opts.cueFired) {
      for (const el of markerEls.values()) {
        const w = el as HTMLElement & { _cueId?: string; _cueDia?: HTMLElement };
        if (!w._cueId || !w._cueDia) continue;
        const fired = opts.cueFired(w._cueId) ? '1' : '';
        if (w._cueDia.dataset.fired === fired) continue;
        w._cueDia.dataset.fired = fired;
        // A fired cue reads as SPENT for this clock-pass: desaturated + dim.
        w._cueDia.style.filter = fired ? 'saturate(0.35) brightness(0.7)' : '';
      }
    }
    const hm = opts.clock.formatHM();
    if (hm !== lastHM) { lastHM = hm; timeChip.textContent = hm; }
    const playLbl = opts.clock.running ? '◼' : '▶';
    if (playLbl !== lastPlayLbl) { lastPlayLbl = playLbl; playBtn.textContent = playLbl; }
    syncMorphToggle();
    if (document.activeElement !== durInput) { const dv = String(opts.state.duration); if (durInput.value !== dv) durInput.value = dv; }
    const era = displayEra();
    if (era !== lastDisplayEra) {
      lastDisplayEra = era;
      // U3: defer the lane re-render while a drag holds pointer capture — re-
      // rendering now would destroy the captured element and strand the edit.
      if (dragInProgress) pendingEraRender = true;
      else { renderCues(); renderAmbience(); renderClips(); }
    }
  }
  refresh();
  // 60Hz-ish refresh via rAF. Cheap — the DOM updates are 4 numeric values.
  let rafHandle = 0;
  const loop = () => {
    refresh();
    rafHandle = requestAnimationFrame(loop);
  };
  rafHandle = requestAnimationFrame(loop);

  // ── hotkeys ────────────────────────────────────────────────────────
  // Register for the cheatsheet right where the keys are handled.
  const unregHotkeys = [
    hotkeyRegistry.register({ keys: 'Space', description: 'play / pause time of day', section: 'navigation', order: 0 }),
    hotkeyRegistry.register({ keys: '[ / ]', description: 'step time ±30 min', section: 'navigation', order: 1 }),
    hotkeyRegistry.register({ keys: ', / .', description: 'step morph ±5%', section: 'navigation', order: 2 }),
    hotkeyRegistry.register({ keys: '⌘S', description: 'save all (soundscape · heroes · atmosphere)', section: 'navigation', order: 3 }),
    hotkeyRegistry.register({ keys: 'Esc', description: 'deselect cue / bed (close editor)', section: 'navigation', order: 4 }),
  ];
  const onKey = (e: KeyboardEvent) => {
    // ⌘S / Ctrl+S — save from anywhere, even mid-edit in a number field. Handled
    // before the input-target + modifier guards below so it always wins.
    if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      saveAllOrSoundscape(); // DM6 — ⌘S flushes EVERY dirty aspect, not just soundscape
      return;
    }
    if (isTextEntryTarget(e)) return; // U2: also bail on focused <select>/contentEditable
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      // U7: during a TIME-lane drag the clock is force-paused with the real
      // running state stashed — Space then toggles the STASH, so the user's
      // intent lands when the drag releases instead of being clobbered.
      if (timeScrubStash !== null) timeScrubStash = !timeScrubStash;
      else opts.clock.running = !opts.clock.running;
    } else if (e.key === '[') {
      opts.clock.t = opts.clock.t - 0.5 / 24;
    } else if (e.key === ']') {
      opts.clock.t = opts.clock.t + 0.5 / 24;
    } else if (e.key === ',') {
      // U4: read the CURRENT morph first, then scrub ±5% — the old idiom
      // retargeted before reading progress, so the first press from past
      // slammed the morph to ~0.95. scrubMorph also preserves ⚙ duration and
      // keeps current/target on the morph's side.
      opts.state.scrubMorph(Math.max(0, morphValue(opts.state) - 0.05));
    } else if (e.key === '.') {
      opts.state.scrubMorph(Math.min(1, morphValue(opts.state) + 0.05));
    } else if (e.key === 'Escape') {
      // U5: Esc deselects the active cue/bed (closing its popover) without
      // needing to re-hit a 9px diamond or risk a stray lane click.
      if (selectedCueId || selectedBedId || selectedClipId || selectedMusicId) {
        selectedCueId = null;
        selectedBedId = null;
        selectedClipId = null;
        selectedMusicId = null; // T5 — Esc also drops a selected music slot
        devBus.emit('hero:select', { id: null });
        renderCues();
        renderAmbience();
        renderClips();
      } else if (pinnedFocus) {
        // B2: a pin set from a chip / 3D click / inspector row has no editor
        // selection to clear, but Esc should still drop the persistent glow —
        // otherwise it was only ever cleared by selecting another hero (the
        // "stuck pinned glow" John flagged). hero:select(null) → pinnedFocus = null.
        devBus.emit('hero:select', { id: null });
      }
    }
  };
  window.addEventListener('keydown', onKey);

  // Safety net: warn before a full reload / tab close drops unsaved cue/bed edits
  // (only when actually dirty — HMR module swaps don't trigger this).
  const onBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (!dirty) return;
    e.preventDefault();
    e.returnValue = '';
  };
  window.addEventListener('beforeunload', onBeforeUnload);

  // ── director mode hide ─────────────────────────────────────────────
  const unsubDev = onDevModeChange((on) => {
    root.style.display = on ? 'flex' : 'none';
  });

  // U1 — pop the last snapshot and restore the live arrays IN PLACE (the engine
  // + lanes hold these refs). Clears selections (the selected item may be gone)
  // and re-renders both lanes; stays dirty so a save still persists the rollback.
  // Shared restore tail for undo + redo (DM8).
  const applyTimelineSnap = (snap: UndoSnap): void => {
    opts.placements.splice(0, opts.placements.length, ...snap.placements);
    opts.cues.splice(0, opts.cues.length, ...snap.cues);
    lastSnapshot = cloneState();
    selectedBedId = null;
    selectedCueId = null;
    selectedClipId = null;
    selectedMusicId = null; // T5
    dirty = true; editGen++; setSaveVis('dirty');
    // DM6 — an undo/redo is just another commit: re-persist + light the readout.
    opts.saveHub?.noteDelegateChanged();
    scheduleAutoSave();
    renderCues();
    renderAmbience(); // also hides a now-stale bed editor (selectedBedId cleared)
    renderClips();
  };
  const undoTimeline = (): boolean => {
    const rec = undoStack.pop();
    if (!rec) return false;
    // DM8 — stash the current (post-edit) state so ⌘⇧Z can re-apply it.
    redoStack.push({ ...cloneState(), at: rec.at });
    applyTimelineSnap(rec);
    return true;
  };
  // DM8 — redo: re-apply the most-recently-undone state, pushing the current
  // (pre-redo) state back onto the undo stack so the two stay paired.
  const redoTimeline = (): boolean => {
    const rec = redoStack.pop();
    if (!rec) return false;
    undoStack.push({ ...cloneState(), at: rec.at });
    if (undoStack.length > UNDO_CAP) undoStack.shift();
    applyTimelineSnap(rec);
    return true;
  };

  document.body.appendChild(root);

  // The strip auto-sizes to its content, so its height is no longer the static
  // TIMELINE_HEIGHT. Publish the LIVE height as `--lec-timeline-h` (kept fresh by
  // a ResizeObserver as clips/beds are authored) so the event log + inspector sit
  // exactly clear of it — no gap when short, no overlap when tall.
  const publishHeight = (): void => {
    document.documentElement.style.setProperty('--lec-timeline-h', `${Math.round(root.offsetHeight)}px`);
  };
  publishHeight();
  const heightObserver = new ResizeObserver(publishHeight);
  heightObserver.observe(root);

  return {
    root,
    undo: undoTimeline,
    redo: redoTimeline,
    lastEditAt,
    lastUndoneAt,
    dispose(): void {
      cancelAnimationFrame(rafHandle);
      heightObserver.disconnect();
      document.documentElement.style.removeProperty('--lec-timeline-h');
      if (autoSaveTimer) clearTimeout(autoSaveTimer); // DM6
      unsubHub?.();                                    // DM6
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onBeforeUnload);
      for (const u of unregHotkeys) u();
      unsubDev();
      unsubSoundPlay();
      unsubSoundStop();
      unsubFocus();
      unsubSelectFocus();
      unsubCueFire();
      unsubArm();
      unsubArmBed();
      unsubLoaded();
      root.remove();
    },
  };
}

// ── helpers ───────────────────────────────────────────────────────────

function mkBtn(label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  Object.assign(b.style, {
    background: 'transparent',
    color: TEXT,
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '4px',
    padding: '2px 6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    lineHeight: '1',
  });
  b.addEventListener('mouseenter', () => { b.style.borderColor = ACCENT; });
  b.addEventListener('mouseleave', () => { b.style.borderColor = 'rgba(255,255,255,0.12)'; });
  return b;
}

function mkLane(label: string): {
  row: HTMLDivElement;
  track: HTMLDivElement;
  fill: HTMLDivElement;
  head: HTMLDivElement;
} {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '24px',
  });
  const lbl = document.createElement('div');
  lbl.textContent = label;
  Object.assign(lbl.style, {
    color: MUTED,
    fontSize: '9px',
    letterSpacing: '0.5px',
    width: '36px',
    flexShrink: '0',
  });
  const track = document.createElement('div');
  Object.assign(track.style, {
    flex: '1 1 auto',
    position: 'relative',
    height: '6px',
    background: TRACK_BG,
    borderRadius: '3px',
    cursor: 'pointer',
  });
  const fill = document.createElement('div');
  Object.assign(fill.style, {
    position: 'absolute',
    left: '0', top: '0', bottom: '0',
    width: '0%',
    background: TRACK_FILL,
    borderRadius: '3px',
    pointerEvents: 'none',
  });
  track.appendChild(fill);
  const head = document.createElement('div');
  Object.assign(head.style, {
    position: 'absolute',
    left: '0%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '10px', height: '10px',
    borderRadius: '50%',
    background: ACCENT,
    boxShadow: '0 0 0 2px rgba(102, 255, 230, 0.18)',
    pointerEvents: 'none',
  });
  track.appendChild(head);
  row.appendChild(lbl);
  row.appendChild(track);
  return { row, track, fill, head };
}

function wireScrub(
  track: HTMLDivElement,
  fill: HTMLDivElement,
  head: HTMLDivElement,
  set: (m: number) => void,
  read: () => number,
  // U7: optional drag-lifecycle hooks so a CALLER can pause/restore state
  // around a scrub (the TIME lane pauses the clock). wireScrub itself stays
  // generic — no blanket pause inside.
  hooks?: { onStart?: () => void; onEnd?: () => void },
): void {
  const pointerToM = (clientX: number): number => {
    const rect = track.getBoundingClientRect();
    const m = (clientX - rect.left) / Math.max(1, rect.width);
    return Math.max(0, Math.min(1, m));
  };
  const onMove = (e: PointerEvent) => {
    const m = pointerToM(e.clientX);
    set(m);
    fill.style.width = `${m * 100}%`;
    head.style.left = `${m * 100}%`;
  };
  track.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    hooks?.onStart?.();
    try { track.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    onMove(e);
    track.addEventListener('pointermove', onMove);
    // U6: one named handler that removes BOTH of its registrations. The old
    // {once:true} pair leaked whichever half didn't fire — a stale onUp from a
    // previous scrub could then kill a live one and call releasePointerCapture
    // unguarded with a dead pointerId.
    const onUp = () => {
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onUp);
      try { track.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      hooks?.onEnd?.(); // U7: covers pointerup AND pointercancel
    };
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
  });
  // Suppress the "read" arg from being optimized away — and silence the
  // warning in builds — by tying it to a one-time init.
  void read;
}

// Make a snap marker (label/dot) an exact-snap control. stopPropagation on
// pointerdown keeps the click from also kicking off a track scrub.
function makeSnap(el: HTMLElement, onSnap: () => void, onHover: (on: boolean) => void): void {
  el.style.pointerEvents = 'auto';
  el.style.cursor = 'pointer';
  el.addEventListener('mouseenter', () => onHover(true));
  el.addEventListener('mouseleave', () => onHover(false));
  el.addEventListener('pointerdown', (e) => { e.stopPropagation(); e.preventDefault(); onSnap(); });
}

// Strip noisy id prefixes for compact lane labels: `sfx_gen_loop` → `gen_loop`,
// `test_music_1` → `music 1`. Shared by the SOUND + CUES lanes.
function prettyAsset(id: string): string {
  return id.replace(/^sfx_/, '').replace(/^test_music_/, 'music ');
}

function morphValue(state: StateController): number {
  // 0 = past, 1 = present.
  // target='present' → m = progress (progress=0 means "starting morph
  //   toward present from past" → visually past).
  // target='past' → m = 1 - progress (progress=0 means "starting morph
  //   toward past from present" → visually present).
  return state.target === 'present' ? state.progress : 1 - state.progress;
}
